/* THE HOME PALETTE — v3 motion engine + interactive modules.
   Engine: GSAP + ScrollTrigger + Lenis (self-hosted, loaded before this file).
   Falls back to IntersectionObserver reveals if the libs are missing, and to
   static content under prefers-reduced-motion. Editor-safe: Lenis is skipped
   in the theme customizer and everything re-inits on section reload.

   Reveal vocabulary (add as attributes, or let autoTag() apply them):
     data-hp-split    headings — split into words, each rises out of a mask
     data-hp-fluid    copy/media — blur + rise fade
     data-hp-clip     images — clip-path wipe up with a slow scale settle
     data-hp-reveal   generic fade + rise
     data-hp-stagger  parent whose children reveal in a batch
     data-hp-parallax background layer, scrub-linked
*/
(function () {
  'use strict';

  var doc = document.documentElement;
  doc.classList.add('hp-js');

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = window.Shopify && window.Shopify.designMode;
  var hasGsap = !reducedMotion && window.gsap && window.ScrollTrigger;

  /* ---- GSAP / Lenis boot ---------------------------------------------- */
  var lenis = null;
  if (hasGsap) {
    doc.classList.add('hp-gsap');
    window.gsap.registerPlugin(window.ScrollTrigger);

    /* Lenis is configured for one thing here — smoothWheel — and a touch
       device has no wheel to smooth. What it does have is a gsap.ticker
       callback running Lenis's virtual-scroll maths on every animation frame,
       and lagSmoothing turned off so none of that work is ever skipped, for a
       behaviour the device cannot express. It also takes over touch scrolling,
       which phones already do natively and better. Skipped on coarse
       pointers: the phone keeps its own scrolling, and gets the frames back. */
    var wantsLenis = !window.matchMedia('(pointer: coarse)').matches;

    if (window.Lenis && !designMode && wantsLenis) {
      try {
        lenis = new window.Lenis({ duration: 1.1, smoothWheel: true });
        doc.classList.add('hp-lenis');
        lenis.on('scroll', window.ScrollTrigger.update);
        window.gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
        window.gsap.ticker.lagSmoothing(0);
      } catch (e) { lenis = null; }
    }
  }
  window.hpLenis = function () { return lenis; };

  /* Phone-lite motion (prototype, 2026-10-01). On phones every reveal keeps
     its slide and fade but drops the blur: a filter: blur() animation is
     repainted on the main thread every frame (it cannot go to the GPU), and
     the page runs dozens of them. Desktop is unchanged. */
  if (hasGsap && window.matchMedia('(max-width: 749px)').matches) {
    (function (G) {
      function noBlur(v) {
        if (!v || typeof v !== 'object' || Array.isArray(v) || !('filter' in v)) return v;
        var c = {};
        for (var k in v) if (k !== 'filter') c[k] = v[k];
        if (typeof c.clearProps === 'string') {
          c.clearProps = c.clearProps.split(',').filter(function (p) { return p.trim() !== 'filter'; }).join(',');
          if (!c.clearProps) delete c.clearProps;
        }
        return c;
      }
      var fromTo = G.fromTo, to = G.to, from = G.from, set = G.set;
      G.fromTo = function (t, a, b) { return fromTo.call(G, t, noBlur(a), noBlur(b)); };
      G.to = function (t, a) { return to.call(G, t, noBlur(a)); };
      G.from = function (t, a) { return from.call(G, t, noBlur(a)); };
      G.set = function (t, a) { return set.call(G, t, noBlur(a)); };
    })(window.gsap);
  }
  /* ---- First paint is never animated -----------------------------------
     The reveal CSS hides [data-hp-reveal] & friends the moment `hp-gsap`
     lands on <html>. That class is added by this file, which sits behind
     ~180KB of deferred GSAP/ScrollTrigger/Lenis — so on a slow connection the
     visitor sees the page paint, then watches it blank out and fade back in.
     That reads as a slow site, not a polished one.

     So: anything already inside the opening viewport when the engine boots is
     revealed instantly and never animated. Only content below the fold earns a
     scroll reveal, which is the only place the motion was ever visible anyway.
     `firstPass` is cleared after the initial run so sections re-rendered by the
     theme editor still animate normally. */
  var firstPass = true;

  function inFirstView(el) {
    if (!firstPass) return false;
    // innerHeight is genuinely 0 in some contexts at DOMContentLoaded — a
    // not-yet-sized iframe, a prerender, some in-app webviews. Measured that
    // happening here, where it silently made every element "below the fold"
    // and animated the whole page anyway. Fall back rather than trust a zero.
    var vh = window.innerHeight ||
             document.documentElement.clientHeight ||
             (window.screen && window.screen.height) ||
             800;
    return el.getBoundingClientRect().top < vh * 0.95;
  }

  function revealNow(el) {
    el.dataset.hpDone = '1';
    // Set opacity directly instead of going through gsap.set + clearProps:
    // these elements are never animated, so there is nothing to clear, and a
    // clearProps list that also wipes the opacity it just set would leave the
    // element hidden by the stylesheet's opacity:0 gate.
    el.style.opacity = '1';
  }

  /* Failsafe: if this file throws after `hp-gsap` is on <html>, every gated
     element would stay at opacity 0 forever — a blank page. Anything still
     unprocessed after 3s gets shown. Elements handled normally carry hpDone,
     so a working page never trips this. */
  if (hasGsap) {
    window.setTimeout(function () {
      document
        .querySelectorAll('[data-hp-reveal], [data-hp-fluid], [data-hp-section], [data-hp-split], [data-hp-clip]')
        .forEach(function (el) {
          if (!el.dataset.hpDone) { el.style.opacity = '1'; el.style.clipPath = 'none'; }
        });
    }, 3000);
  }

  /* ---- Overlay scroll lock ---------------------------------------------
     Lenis drives the page with its own virtual scroll, so `overflow: hidden`
     on <body> does not stop it — a wheel over an open drawer or modal would
     keep scrolling the page behind. Dawn adds `.overflow-hidden` to <body>
     whenever it opens the cart drawer, menu drawer or a modal, so we mirror
     that: pause Lenis while any overlay is open, resume when it closes.
     Nested scrollers are additionally marked `data-lenis-prevent`, which tells
     Lenis to leave wheel/touch events inside them alone.
  ---------------------------------------------------------------------- */
  function initOverlayScrollLock() {
    if (!lenis) return;
    var body = document.body;
    var locked = false;

    var sync = function () {
      var open = body.classList.contains('overflow-hidden') ||
        body.classList.contains('overflow-hidden-mobile') ||
        body.classList.contains('overflow-hidden-tablet');
      if (open === locked) return;
      locked = open;
      if (open) { lenis.stop(); } else { lenis.start(); }
    };

    new MutationObserver(sync).observe(body, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  /* Mark every in-app scroll container so Lenis never steals its wheel. */
  function markScrollContainers(root) {
    (root || document).querySelectorAll(
      '.hp-drawer-scroll, .cart-drawer .drawer__inner, .menu-drawer__inner, ' +
      '.quick-add-modal__content, .product-popup-modal__content, .hp-lightbox'
    ).forEach(function (el) {
      if (!el.hasAttribute('data-lenis-prevent')) el.setAttribute('data-lenis-prevent', '');
    });
  }

  /* ---- Word splitter ---------------------------------------------------
     Walks text nodes only, so inline markup (<em>, <a>, <br>) survives.
     Each word becomes <span class="hp-w"><span class="hp-w__i">word</span></span>
  ---------------------------------------------------------------------- */
  function splitWords(el) {
    if (!el || el.dataset.hpSplitDone) return [];
    el.dataset.hpSplitDone = '1';

    var inners = [];
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    var n;
    while ((n = walker.nextNode())) {
      if (n.nodeValue && n.nodeValue.trim()) textNodes.push(n);
    }
    // bail on very long copy — masking hundreds of words is wasteful
    var totalWords = textNodes.reduce(function (a, t) {
      return a + t.nodeValue.trim().split(/\s+/).length;
    }, 0);
    if (totalWords === 0 || totalWords > 60) return [];

    textNodes.forEach(function (node) {
      var frag = document.createDocumentFragment();
      var parts = node.nodeValue.split(/(\s+)/);
      parts.forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        var mask = document.createElement('span');
        mask.className = 'hp-w';
        var inner = document.createElement('span');
        inner.className = 'hp-w__i';
        inner.textContent = part;
        mask.appendChild(inner);
        frag.appendChild(mask);
        inners.push(inner);
      });
      node.parentNode.replaceChild(frag, node);
    });

    if (inners.length) el.classList.add('hp-split-ready');
    return inners;
  }

  /* ---- Auto-tagging ----------------------------------------------------
     Gives the whole site the same fluid entrance without editing every
     template. Skips chrome (header/footer/nav), the hero (it runs its own
     timeline), and anything already tagged.
  ---------------------------------------------------------------------- */
  var SKIP = [
    '.hp-hero', 'header', 'footer', 'nav', '.header-wrapper', '.footer',
    '.hp-announce', '.hp-marquee', '.cart-drawer', '.quick-add-modal',
    '.search-modal', '.menu-drawer', '.hp-vrev',
    // cards already animate as part of their stagger batch — splitting their
    // titles too would hide the text behind a second, competing animation
    '[data-hp-stagger]', '.card', '.card-wrapper', '.hp-drec',
    '[data-hp-no-anim]'
  ].join(', ');

  function autoTag(root) {
    root = root || document;
    if (!root.querySelectorAll) return;

    // headings → word-split reveal
    root.querySelectorAll('h1, h2, h3, .hp-section-head__title').forEach(function (el) {
      if (el.closest(SKIP)) return;
      if (el.dataset.hpSplit !== undefined || el.dataset.hpSplitDone) return;
      if (el.hasAttribute('data-hp-reveal')) el.removeAttribute('data-hp-reveal');
      el.dataset.hpSplit = '';
    });

    // supporting copy → blur emerge
    root.querySelectorAll('.hp-section-head__text, .rte > p, .hp-story__text, .hp-cta__text, .hp-eyebrow')
      .forEach(function (el) {
        if (el.closest(SKIP)) return;
        if (el.dataset.hpFluid !== undefined || el.dataset.hpReveal !== undefined) return;
        el.dataset.hpFluid = '';
      });

    // every remaining section on every page gets a directional reveal, so the
    // whole site scrolls with the same language — not just the custom sections
    var scope = root.querySelectorAll ? root : document;
    (scope.querySelectorAll('main .shopify-section') || []).forEach(function (sec, i) {
      if (sec.closest(SKIP)) return;
      if (sec.dataset.hpSection !== undefined || sec.dataset.hpDone) return;
      // never hide the first section — it is above the fold and would flash
      if (i === 0 || !sec.previousElementSibling) return;
      // the hero runs its own timeline; tagging its wrapper would fight it
      if (sec.querySelector('.hp-hero, [data-hp-no-anim]')) return;
      // skip sections that already animate their own contents
      if (sec.querySelector('[data-hp-reveal], [data-hp-stagger], [data-hp-split], [data-hp-fluid]')) return;
      sec.dataset.hpSection = '';
    });
  }

  /* ---- Direction helper -------------------------------------------------
     Sections reveal *from* the side they sit on: a left-hand column slides in
     from the left, a right-hand one from the right, full-width bands rise.
  ---------------------------------------------------------------------- */
  function autoDirection(el) {
    var explicit = el.getAttribute('data-hp-dir');
    if (explicit) return explicit;

    var host = el.closest('.shopify-section, section') || document.body;
    var hostBox = host.getBoundingClientRect();
    var box = el.getBoundingClientRect();
    if (!hostBox.width || !box.width) return 'up';

    // full-width-ish content just rises
    if (box.width > hostBox.width * 0.72) return 'up';

    var centre = (box.left + box.width / 2 - hostBox.left) / hostBox.width;
    if (centre < 0.4) return 'left';
    if (centre > 0.6) return 'right';
    return 'up';
  }

  function fromVars(dir, dist) {
    dist = dist || 56;
    switch (dir) {
      case 'left':  return { x: -dist, y: 0 };
      case 'right': return { x: dist, y: 0 };
      case 'down':  return { x: 0, y: -dist };   // enters from above
      default:      return { x: 0, y: dist };    // 'up' — enters from below
    }
  }

  /* ---- Typewriter --------------------------------------------------------
     Reveals characters in sequence behind a blinking caret. Used sparingly —
     opt in with data-hp-type.
  ---------------------------------------------------------------------- */
  function typewriter(el) {
    if (el.dataset.hpTypeDone) return;
    el.dataset.hpTypeDone = '1';
    var full = el.textContent;
    if (!full.trim() || full.length > 90) return;

    el.textContent = '';
    el.classList.add('hp-typing');
    var i = 0;
    var speed = parseInt(el.getAttribute('data-hp-type'), 10) || 38;
    (function tick() {
      el.textContent = full.slice(0, i);
      if (i++ <= full.length) {
        window.setTimeout(tick, speed);
      } else {
        el.classList.remove('hp-typing');
      }
    })();
  }

  /* ---- Reveals + stacking cards + parallax ----------------------------- */
  function initMotion(root) {
    root = root || document;
    autoTag(root);

    if (hasGsap) {
      var gsap = window.gsap;
      var ST = window.ScrollTrigger;

      /* Measure first, write after. Each element used to be measured
         (inFirstView / autoDirection read its box) and then written (split
         into words, revealed, handed to GSAP) before the next was measured —
         and every measurement after a write makes the browser lay the whole
         page out again. On the home page that was hundreds of full layouts on
         load (PageSpeed: "forced reflow", ScrollTrigger ≈4.7s of main thread
         on a throttled phone). Same elements, same order, same animations —
         the reads simply all happen before the first write. */
      function pending(sel) {
        return Array.prototype.filter.call(root.querySelectorAll(sel), function (el) {
          return !el.dataset.hpDone;
        });
      }
      var splits = pending('[data-hp-split]');
      var types = pending('[data-hp-type]');
      var fluids = pending('[data-hp-fluid]');
      var clips = pending('[data-hp-clip]');
      var staggerKids = [];
      root.querySelectorAll('[data-hp-stagger]').forEach(function (parent) {
        Array.prototype.forEach.call(parent.children, function (child) {
          if (child.hasAttribute('data-hp-reveal') && !child.dataset.hpDone) staggerKids.push(child);
        });
      });
      var reveals = pending('[data-hp-reveal]').filter(function (el) { return staggerKids.indexOf(el) === -1; });
      var sections = pending('[data-hp-section]');

      // read phase — no writes until every box has been measured
      var inView = new Map();
      var dirOf = new Map();
      [splits, fluids, clips, staggerKids, reveals, sections].forEach(function (list) {
        list.forEach(function (el) { if (!inView.has(el)) inView.set(el, inFirstView(el)); });
      });
      [splits, fluids, reveals, sections].forEach(function (list) {
        list.forEach(function (el) { if (!inView.get(el) && !dirOf.has(el)) dirOf.set(el, autoDirection(el)); });
      });
      function dirFor(el) { return dirOf.has(el) ? dirOf.get(el) : 'up'; }

      /* One-time reveals start when the element's top crosses N% of the
         viewport — the same rule ScrollTrigger's start: 'top N%' applied. They
         run on IntersectionObserver instead: ScrollTrigger re-measured every one
         of these (hundreds on the home page) on each refresh, each measurement a
         full-page layout, which is where most of the page's main-thread time
         went. An observer reports entries without forcing layout. Elements
         already scrolled past (a reload halfway down) reveal at once, as before.
         The background parallax below still uses ScrollTrigger (scrubbed). */
      var observers = {};
      function onReveal(el, pct, play) {
        var key = String(pct);
        if (!observers[key]) {
          var cbs = new Map();
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (e) {
              if (e.isIntersecting || e.boundingClientRect.bottom < 0) {
                io.unobserve(e.target);
                var fns = cbs.get(e.target);
                cbs.delete(e.target);
                if (fns) fns.forEach(function (fn) { fn(); });
                return;
              }
              /* Inside a sideways-scrolling row, items past its right edge are
                 clipped by the row, so they never "intersect" however far the
                 page scrolls. ScrollTrigger only ever looked at the vertical
                 position — so watch the row itself for these instead. */
              var r = e.boundingClientRect;
              if (r.width && (r.left >= window.innerWidth || r.right <= 0)) {
                var row = e.target.parentElement;
                while (row && row !== document.body && !(row.scrollWidth > row.clientWidth + 1)) row = row.parentElement;
                if (row && row !== document.body) {
                  io.unobserve(e.target);
                  var moved = cbs.get(e.target) || [];
                  cbs.delete(e.target);
                  cbs.set(row, (cbs.get(row) || []).concat(moved));
                  io.observe(row);
                }
              }
            });
          }, { rootMargin: '0px 0px -' + (100 - pct) + '% 0px' });
          observers[key] = { io: io, cbs: cbs };
        }
        observers[key].cbs.set(el, (observers[key].cbs.get(el) || []).concat([play]));
        observers[key].io.observe(el);
      }
      function revealTween(el, pct, from, to) {
        to.paused = true;
        var tw = gsap.fromTo(el, from, to);
        onReveal(el, pct, function () { tw.play(); });
      }

      // 1. word-split headings — CASCADE DROP + BLUR EMERGE
      splits.forEach(function (el) {
        if (el.dataset.hpDone) return;
        if (inView.get(el)) { revealNow(el); return; }
        el.dataset.hpDone = '1';
        var words = splitWords(el);
        if (!words.length) {
          var f = fromVars(dirFor(el), 40);
          revealTween(el, 88, { x: f.x, y: f.y, opacity: 0, filter: 'blur(10px)' }, {
            x: 0, y: 0, opacity: 1, filter: 'blur(0px)', duration: 1, ease: 'power3.out',
            clearProps: 'filter,transform'
          });
          return;
        }
        // words fall from above, each blurring into focus — the cascade
        var wordsTw = gsap.fromTo(words,
          { yPercent: -110, opacity: 0, filter: 'blur(12px)' },
          {
            yPercent: 0, opacity: 1, filter: 'blur(0px)',
            duration: 1.05, ease: 'power4.out', stagger: 0.055,
            clearProps: 'filter', paused: true
          });
        onReveal(el, 88, function () { wordsTw.play(); });
      });

      // 2. typewriter (opt-in)
      types.forEach(function (el) {
        if (el.dataset.hpDone) return;
        el.dataset.hpDone = '1';
        onReveal(el, 88, function () { typewriter(el); });
      });

      // 3. fluid copy/media — BLUR EMERGE, direction-aware
      fluids.forEach(function (el) {
        if (el.dataset.hpDone) return;
        if (inView.get(el)) { revealNow(el); return; }
        el.dataset.hpDone = '1';
        var f = fromVars(dirFor(el), 34);
        revealTween(el, 90,
          { x: f.x, y: f.y, opacity: 0, filter: 'blur(12px)' },
          {
            x: 0, y: 0, opacity: 1, filter: 'blur(0px)',
            duration: 1.05, ease: 'power3.out', clearProps: 'filter,transform'
          });
      });

      // 4. clip-path image wipes
      clips.forEach(function (el) {
        if (el.dataset.hpDone) return;
        if (inView.get(el)) {
          revealNow(el);
          el.style.clipPath = 'none';
          return;
        }
        el.dataset.hpDone = '1';
        var img = el.querySelector('img') || el;
        var tl = gsap.timeline({ paused: true });
        onReveal(el, 88, function () { tl.play(); });
        tl.fromTo(el, { clipPath: 'inset(0 0 100% 0)' },
          { clipPath: 'inset(0 0 0% 0)', duration: 1.2, ease: 'power4.out', clearProps: 'clipPath' });
        if (img !== el) {
          tl.fromTo(img, { scale: 1.18 }, { scale: 1, duration: 1.6, ease: 'power3.out', clearProps: 'transform' }, 0);
        }
      });

      // 5. stagger groups — cards cascade in, blurring into place
      var staggerChildren = [];
      staggerKids.forEach(function (child) {
        if (child.dataset.hpDone) return;
        if (inView.get(child)) { revealNow(child); return; }
        child.dataset.hpDone = '1';
        staggerChildren.push(child);
      });
      if (staggerChildren.length) {
        // cards that enter together cascade together, as ST.batch did
        var pendingBatch = [];
        var flushBatch = function () {
          var batch = pendingBatch;
          pendingBatch = [];
          gsap.fromTo(batch,
            { y: 64, opacity: 0, scale: 0.95, filter: 'blur(10px)' },
            { y: 0, opacity: 1, scale: 1, filter: 'blur(0px)', duration: 0.95, ease: 'power3.out',
              stagger: 0.075, overwrite: true, clearProps: 'transform,filter' });
        };
        gsap.set(staggerChildren, { y: 64, opacity: 0, scale: 0.95, filter: 'blur(10px)' });
        staggerChildren.forEach(function (child) {
          onReveal(child, 90, function () {
            if (!pendingBatch.length) requestAnimationFrame(flushBatch);
            pendingBatch.push(child);
          });
        });
      }

      // 6. generic reveals — direction-aware
      reveals.forEach(function (el) {
        if (el.dataset.hpDone) return;
        if (inView.get(el)) { revealNow(el); return; }
        el.dataset.hpDone = '1';
        var f = fromVars(dirFor(el));
        revealTween(el, 88,
          { x: f.x, y: f.y, opacity: 0, filter: 'blur(8px)' },
          { x: 0, y: 0, opacity: 1, filter: 'blur(0px)', duration: 1, ease: 'power3.out',
            clearProps: 'transform,filter' });
      });

      // 7. whole-section reveal for anything not otherwise animated
      sections.forEach(function (el) {
        if (el.dataset.hpDone) return;
        if (inView.get(el)) { revealNow(el); return; }
        el.dataset.hpDone = '1';
        var f = fromVars(dirFor(el), 48);
        revealTween(el, 92,
          { x: f.x, y: f.y, opacity: 0 },
          { x: 0, y: 0, opacity: 1, duration: 1.05, ease: 'power3.out', clearProps: 'transform' });
      });

      // 8. background parallax
      root.querySelectorAll('[data-hp-parallax]').forEach(function (el) {
        if (el.dataset.hpParDone) return;
        el.dataset.hpParDone = '1';
        var holder = el.closest('section, .shopify-section') || el.parentElement;
        gsap.fromTo(el, { yPercent: -6 }, {
          yPercent: 6, ease: 'none',
          scrollTrigger: { trigger: holder, start: 'top bottom', end: 'bottom top', scrub: true }
        });
      });

      ST.refresh();
      firstPass = false;
      return;
    }

    // ---- vanilla fallback (no GSAP) ----
    root.querySelectorAll('[data-hp-stagger]').forEach(function (parent) {
      Array.prototype.forEach.call(parent.children, function (child, i) {
        child.style.setProperty('--hp-i', i);
      });
    });
    var sel = '[data-hp-reveal]:not(.hp-inview), [data-hp-fluid]:not(.hp-inview), ' +
              '[data-hp-split]:not(.hp-inview), [data-hp-section]:not(.hp-inview)';
    var nodes = root.querySelectorAll(sel);
    if (!nodes.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('hp-inview'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('hp-inview');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---- Sticky header: shadow + announcement offset --------------------- */
  function initStickyChrome() {
    var wrapper = document.querySelector('.header-wrapper');
    var announce = document.querySelector('.hp-announce');

    function measure() {
      var h = announce ? announce.offsetHeight : 0;
      doc.style.setProperty('--hp-announce-h', h + 'px');
      var header = wrapper ? wrapper.offsetHeight : 0;
      doc.style.setProperty('--hp-header-h', (h + header) + 'px');
      // Dawn uses --header-height for the menu drawer and sticky offsets; with
      // its own sticky-header disabled we own this value now.
      if (header) doc.style.setProperty('--header-height', header + 'px');
    }
    measure();
    window.addEventListener('resize', measure, { passive: true });
    if (window.ResizeObserver && announce) new ResizeObserver(measure).observe(announce);

    if (!wrapper) return;
    var update = function () {
      wrapper.classList.toggle('hp-scrolled', window.scrollY > 20);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---- Video lightbox --------------------------------------------------- */
  var lightbox = null;
  function getLightbox() {
    if (lightbox) return lightbox;
    var el = document.createElement('div');
    el.className = 'hp-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Video player');
    el.innerHTML =
      '<div class="hp-lightbox__inner">' +
      '<button type="button" class="hp-lightbox__close" aria-label="Close video">' +
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
      '</button>' +
      '<video controls playsinline preload="metadata"></video>' +
      '</div>';
    document.body.appendChild(el);

    var video = el.querySelector('video');
    var opener = null;
    function close() {
      el.classList.remove('is-open');
      video.pause();
      video.removeAttribute('src');
      video.load();
      if (lenis) lenis.start();
      if (opener && opener.focus) opener.focus();
      opener = null;
    }
    el.addEventListener('click', function (e) { if (e.target === el) close(); });
    el.querySelector('.hp-lightbox__close').addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.classList.contains('is-open')) close();
    });

    lightbox = {
      open: function (src, wide, from) {
        opener = from || null;
        el.querySelector('.hp-lightbox__inner').classList.toggle('hp-lightbox__inner--wide', !!wide);
        video.src = src;
        el.classList.add('is-open');
        if (lenis) lenis.stop();
        var play = video.play();
        if (play && play.catch) play.catch(function () {});
        el.querySelector('.hp-lightbox__close').focus();
      }
    };
    return lightbox;
  }

  function initVideoTriggers(root) {
    (root || document).querySelectorAll('[data-hp-video-src]').forEach(function (btn) {
      if (btn.dataset.hpVideoWired) return;
      btn.dataset.hpVideoWired = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        getLightbox().open(btn.getAttribute('data-hp-video-src'), btn.hasAttribute('data-hp-video-wide'), btn);
      });
    });
  }

  /* ---- Full-height hero slider ----------------------------------------- */
  var HpHero = (function () {
    function HpHero() { return Reflect.construct(HTMLElement, [], HpHero); }
    HpHero.prototype = Object.create(HTMLElement.prototype, { constructor: { value: HpHero } });
    Object.setPrototypeOf(HpHero, HTMLElement);

    HpHero.prototype.connectedCallback = function () {
      var el = this;
      el.slides = Array.prototype.slice.call(el.querySelectorAll('.hp-hero__slide'));
      if (el.slides.length === 0) return;
      el.dots = Array.prototype.slice.call(el.querySelectorAll('.hp-hero__dot'));
      el.counter = el.querySelector('.hp-hero__counter-current');
      el.index = 0;
      el.timer = null;
      el.autoplay = el.dataset.autoplay === 'true' && !reducedMotion;
      el.speed = (parseFloat(el.dataset.speed) || 6) * 1000;

      var prev = el.querySelector('.hp-hero__arrow--prev');
      var next = el.querySelector('.hp-hero__arrow--next');
      if (prev) prev.addEventListener('click', function () { el.go(el.index - 1, true); });
      if (next) next.addEventListener('click', function () { el.go(el.index + 1, true); });
      el.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { el.go(i, true); });
      });

      var startX = null;
      el.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      el.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 48) el.go(el.index + (dx < 0 ? 1 : -1), true);
        startX = null;
      }, { passive: true });

      el.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') el.go(el.index - 1, true);
        if (e.key === 'ArrowRight') el.go(el.index + 1, true);
      });

      document.addEventListener('visibilitychange', function () {
        document.hidden ? el.stop() : el.play();
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries[0].isIntersecting ? el.play() : el.stop();
        }, { threshold: 0.15 }).observe(el);
      }

      /* The background clip is NOT started here. This runs as soon as the
         element upgrades, which is before initBgVideo has decided whether this
         screen wants the desktop cut or the phone one — and a play() call at
         this point starts whatever file the markup happens to be holding, which
         is the desktop cut. That is how a phone ended up showing the landscape
         clip: the eager play() won the race, and whether initBgVideo's swap
         landed afterwards depended on timing and on the connection check, so
         the same phone could get either video on different loads. Source
         choice and playback both belong to initBgVideo, and only there. */
      var vid = el.querySelector('.hp-hero__video video');
      if (vid) vid.muted = true;

      el.render();
      el.play();
    };

    HpHero.prototype.go = function (i, user) {
      var n = this.slides.length;
      this.index = ((i % n) + n) % n;
      this.render();
      if (user) { this.stop(); this.play(); }
    };

    HpHero.prototype.render = function () {
      var idx = this.index;
      this.slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === idx);
        slide.setAttribute('aria-hidden', i === idx ? 'false' : 'true');
      });
      this.dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === idx);
        dot.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });
      if (this.counter) {
        this.counter.textContent = (idx + 1 < 10 ? '0' : '') + (idx + 1);
      }
    };

    HpHero.prototype.play = function () {
      var el = this;
      if (!el.autoplay || el.timer || el.slides.length < 2) return;
      el.timer = setInterval(function () { el.go(el.index + 1); }, el.speed);
    };

    HpHero.prototype.stop = function () {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    };

    return HpHero;
  })();
  if (!customElements.get('hp-hero')) customElements.define('hp-hero', HpHero);

  /* ---- Horizontal rail with arrows -------------------------------------- */
  var HpRail = (function () {
    function HpRail() { return Reflect.construct(HTMLElement, [], HpRail); }
    HpRail.prototype = Object.create(HTMLElement.prototype, { constructor: { value: HpRail } });
    Object.setPrototypeOf(HpRail, HTMLElement);

    HpRail.prototype.connectedCallback = function () {
      var el = this;
      var track = el.querySelector('.hp-rail__track');
      if (!track) return;
      var prev = el.querySelector('.hp-rail__arrow--prev');
      var next = el.querySelector('.hp-rail__arrow--next');
      function step() { return Math.max(track.clientWidth * 0.7, 240); }
      function update() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }
      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: reducedMotion ? 'auto' : 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: reducedMotion ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    };
    return HpRail;
  })();
  if (!customElements.get('hp-rail')) customElements.define('hp-rail', HpRail);

  /* ---- <hp-colour-shop> — Shop by Colour ---------------------------------
     Every colour's products are rendered into the page up front and this just
     swaps which group is visible.

     It used to fetch each colour through the Section Rendering API. That could
     never work: the API resolves ?section_id= against the template of the URL
     being requested, and this section only lives in index.json — so requesting
     it at a /collections or /search URL always came back without the section
     and the grid stayed empty. Toggling locally is also instant, needs no
     network, and cannot half-fail.
  ---------------------------------------------------------------------- */
  var HpColourShop = (function () {
    function HpColourShop() { return Reflect.construct(HTMLElement, [], HpColourShop); }
    HpColourShop.prototype = Object.create(HTMLElement.prototype, { constructor: { value: HpColourShop } });
    Object.setPrototypeOf(HpColourShop, HTMLElement);

    HpColourShop.prototype.connectedCallback = function () {
      var el = this;
      el.swatches = Array.prototype.slice.call(el.querySelectorAll('[data-hp-colour]'));
      el.titleEl = el.querySelector('[data-hp-colour-title]');
      el.linkEl = el.querySelector('[data-hp-colour-link]');
      if (!el.swatches.length || !el.groups().length) return;

      /* On the home page only the first colour is built into the HTML; the
         others wait in empty <template data-hp-colour-remote> slots and come
         from /?view=colours (the same section, all colours, no layout). Start
         that fetch as the section nears the screen so a tap is still instant. */
      if (el.querySelector('template[data-hp-colour-remote]') && 'IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          if (!entries.some(function (e) { return e.isIntersecting; })) return;
          io.disconnect();
          el.remote();
        }, { rootMargin: '100% 0px' });
        io.observe(el);
      }

      el.swatches.forEach(function (btn) {
        btn.addEventListener('click', function () { el.select(btn); });
      });

      /* A ?colour= in the URL wins over the template's default. This is what
         "View all" links to: the destination page runs this same section, so
         deep-linking the colour means the visitor lands on exactly the set of
         products the swatch they clicked was showing — no search, no second
         matching rule that could disagree with the first. Both spellings are
         accepted so a hand-written link works either way. */
      var wanted = null;
      try {
        var params = new URLSearchParams(window.location.search);
        var asked = params.get('colour') || params.get('color');
        if (asked) {
          asked = asked.toLowerCase();
          wanted = el.swatches.filter(function (b) {
            return b.getAttribute('data-hp-colour-key') === asked;
          })[0] || null;
        }
      } catch (e) { wanted = null; }

      /* Otherwise start on whichever swatch the template marked active, so the
         visible group and the highlighted swatch agree on first paint. */
      var initial = wanted || el.swatches.filter(function (b) {
        return b.classList.contains('is-active');
      })[0] || el.swatches[0];
      el.select(initial, true);
    };

    /* Colour keys come from `handleize`, so they are already safe in a
       selector — but CSS.escape is what guarantees it, and the fallback keeps
       older browsers working rather than throwing on the querySelector. */
    function hpCssEscape(s) {
      return (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&');
    }

    /* The groups that are actually IN the document. Only the colour the
       template marked active starts there; the rest arrive on first use, so
       this is read fresh each time rather than cached at connect. */
    HpColourShop.prototype.groups = function () {
      return Array.prototype.slice.call(this.querySelectorAll('[data-hp-colour-group]'));
    };

    /* Lift one colour's cards out of its <template> and into the document, in
       place, so the swap keeps the authored order. Cheap and one-way: the
       template is consumed, and a second click finds a real group already
       there. Cards inside a template were never upgraded or revealed, so the
       usual passes are run over the group once it lands — the quick-add and
       product-form elements inside it define themselves on insertion.

       Returns the group element, or null when there was no such colour. */
    /* The other colours, fetched once and shared by every later tap. */
    HpColourShop.prototype.remote = function () {
      if (!this._remote) {
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        this._remote = fetch(root + '?view=colours', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); })
          .catch(function () { return null; });
      }
      return this._remote;
    };

    /* Fill an empty remote slot from the fetched page. Resolves true when the
       colour's group is now in its <template>, ready for hydrate(). */
    HpColourShop.prototype.fillRemote = function (key) {
      var el = this;
      return el.remote().then(function (doc) {
        var tpl = el.querySelector('template[data-hp-colour-defer="' + hpCssEscape(key) + '"][data-hp-colour-remote]');
        if (!tpl) return true;
        var src = doc && doc.querySelector('[data-hp-colour-group="' + hpCssEscape(key) + '"]');
        if (!src) {
          var srcTpl = doc && doc.querySelector('template[data-hp-colour-defer="' + hpCssEscape(key) + '"]');
          src = srcTpl && srcTpl.content.querySelector('[data-hp-colour-group]');
        }
        if (!src) return false;
        tpl.content.appendChild(document.importNode(src, true));
        tpl.removeAttribute('data-hp-colour-remote');
        return true;
      });
    };

    HpColourShop.prototype.hydrate = function (key) {
      var tpl = this.querySelector('template[data-hp-colour-defer="' + hpCssEscape(key) + '"]');
      if (!tpl) return null;
      var frag = tpl.content.cloneNode(true);
      var group = frag.querySelector('[data-hp-colour-group]');
      tpl.parentNode.replaceChild(frag, tpl);
      /* The section just got taller by however many cards this colour has, so
         every trigger positioned below it is now measuring against the old
         page height. */
      if (hasGsap) window.ScrollTrigger.refresh();
      return group;
    };

    HpColourShop.prototype.select = function (btn, initial) {
      var el = this;
      var key = btn.getAttribute('data-hp-colour-key');

      /* Bring this colour into the document before anything looks for it. */
      if (!el.querySelector('[data-hp-colour-group="' + hpCssEscape(key) + '"]')) {
        if (el.querySelector('template[data-hp-colour-defer="' + hpCssEscape(key) + '"][data-hp-colour-remote]')) {
          // not fetched yet: wait for it (normally already arrived), then retry
          if (el._waiting === key) return;
          el._waiting = key;
          el.setAttribute('aria-busy', 'true');
          el.fillRemote(key).then(function (ok) {
            el.removeAttribute('aria-busy');
            if (el._waiting !== key) return;
            el._waiting = null;
            // fetch failed: the colour's own page shows the same products
            if (!ok && !initial && btn.getAttribute('data-hp-url')) { window.location.href = btn.getAttribute('data-hp-url'); return; }
            if (!el.querySelector('template[data-hp-colour-defer="' + hpCssEscape(key) + '"][data-hp-colour-remote]')) el.select(btn, initial);
          });
          return;
        }
        el.hydrate(key);
      }

      el.swatches.forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      var shownGroup = null;
      el.groups().forEach(function (g) {
        var on = g.getAttribute('data-hp-colour-group') === key;
        g.hidden = !on;
        if (on) shownGroup = g;
      });

      if (el.titleEl) el.titleEl.textContent = btn.getAttribute('data-hp-label') || '';
      /* A colour with no destination hides the link rather than leaving the
         previous colour's. Without the else branch, switching from a colour
         that has a "View all" to one that does not left the old href in place,
         so the link silently pointed at the wrong colour. */
      var url = btn.getAttribute('data-hp-url');
      if (el.linkEl) {
        if (url) {
          el.linkEl.href = url;
          el.linkEl.hidden = false;
        } else {
          el.linkEl.removeAttribute('href');
          el.linkEl.hidden = true;
        }
      }

      if (!shownGroup || initial) return;

      /* Reveal-on-scroll already ran for these cards while they were hidden, so
         re-running initMotion would do nothing. Animate the swap directly. */
      if (hasGsap && !reducedMotion) {
        window.gsap.fromTo(shownGroup.children,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out', stagger: 0.04, clearProps: 'transform,opacity' });
      }
    };

    return HpColourShop;
  })();
  if (!customElements.get('hp-colour-shop')) customElements.define('hp-colour-shop', HpColourShop);

  /* ---- <hp-recommendations> --------------------------------------------
     Shopify's native Product Recommendations API. It is powered by real
     order/behaviour data for this shop, so the results are genuinely
     relevant to the product in context — and it needs no API key, no
     third-party call, and no secret in the theme.
       intent=related        "you may also like"
       (intent=complementary is rendered in Liquid from Product pairing
        entries, so those bands carry no data-url and never fetch)
  ---------------------------------------------------------------------- */
  var HpRecs = (function () {
    function HpRecs() { return Reflect.construct(HTMLElement, [], HpRecs); }
    HpRecs.prototype = Object.create(HTMLElement.prototype, { constructor: { value: HpRecs } });
    Object.setPrototypeOf(HpRecs, HTMLElement);

    HpRecs.prototype.connectedCallback = function () {
      var el = this;
      if (el.dataset.hpLoaded) return;
      var url = el.getAttribute('data-url');
      var target = el.querySelector('[data-hp-recs-grid]');
      if (!url || !target) return;

      var run = function () {
        el.dataset.hpLoaded = '1';
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var grid = parsed.querySelector('[data-hp-recs-source]');
            if (!grid || !grid.children.length) { el.remove(); return; }
            target.innerHTML = grid.innerHTML;
            el.hidden = false;
            el.classList.add('is-loaded');
            initMotion(el);
            if (hasGsap) window.ScrollTrigger.refresh();
          })
          .catch(function () { el.remove(); });
      };

      // only fetch when it is about to matter
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) { io.disconnect(); run(); }
        }, { rootMargin: '600px' });
        io.observe(el);
      } else { run(); }
    };
    return HpRecs;
  })();
  if (!customElements.get('hp-recommendations')) customElements.define('hp-recommendations', HpRecs);

  /* ---- <hp-drawer-recs> -------------------------------------------------
     The cart drawer opens on every page, where no recommendations *section*
     is guaranteed to exist — so it uses the JSON recommendations endpoint and
     renders compact cards itself. Re-runs whenever the cart changes so the
     suggestions follow whatever was added last.
  ---------------------------------------------------------------------- */
  var HpDrawerRecs = (function () {
    function HpDrawerRecs() { return Reflect.construct(HTMLElement, [], HpDrawerRecs); }
    HpDrawerRecs.prototype = Object.create(HTMLElement.prototype, { constructor: { value: HpDrawerRecs } });
    Object.setPrototypeOf(HpDrawerRecs, HTMLElement);

    function money(cents) {
      var fmt = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'PKR';
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency', currency: fmt, maximumFractionDigits: 0
        }).format(cents / 100);
      } catch (e) { return (cents / 100).toFixed(0); }
    }

    HpDrawerRecs.prototype.connectedCallback = function () { this.load(); };

    HpDrawerRecs.prototype.load = function () {
      var el = this;
      var pid = el.getAttribute('data-product-id');
      var limit = el.getAttribute('data-limit') || 4;
      var grid = el.querySelector('[data-hp-recs-grid]');
      if (el.hasAttribute('data-curated')) { el.hidden = false; return; }   // pairings rendered in Liquid
      if (!pid || !grid) { el.hidden = true; return; }
      if (el.dataset.hpFor === pid) return;   // already showing this product's set
      el.dataset.hpFor = pid;

      fetch('/recommendations/products.json?product_id=' + pid + '&limit=' + limit + '&intent=related')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var items = (data && data.products) || [];
          if (!items.length) { el.hidden = true; return; }
          grid.innerHTML = items.map(function (p) {
            var img = p.featured_image || (p.images && p.images[0]) || '';
            var src = img ? img.replace(/(\.[a-z]+)(\?.*)?$/i, '_300x$1') : '';
            var price = p.price_min != null ? p.price_min : p.price;
            return '<a class="hp-drec" href="' + p.url + '">' +
              '<span class="hp-drec__media">' +
                (src ? '<img src="' + src + '" alt="" loading="lazy" width="150" height="188">' : '') +
              '</span>' +
              '<span class="hp-drec__title">' + p.title + '</span>' +
              '<span class="hp-drec__price">' + money(price) + '</span>' +
            '</a>';
          }).join('');
          el.hidden = false;
        })
        .catch(function () { el.hidden = true; });
    };

    return HpDrawerRecs;
  })();
  if (!customElements.get('hp-drawer-recs')) customElements.define('hp-drawer-recs', HpDrawerRecs);

  /* ---- Reviews: brand styling + pagination -------------------------------
     The review app may render either into the page (light DOM) or inside a
     Shadow DOM, depending on which widget version the shop is on. Theme CSS
     cannot cross a shadow boundary, so we walk any open shadow roots, inject
     the brand styles there, and run pagination inside whichever root actually
     holds the reviews. Everything is defensive — if the app renders nothing we
     recognise, we leave it completely alone.
  ---------------------------------------------------------------------- */
  function hpReviewCss(starColor) {
    var gold = starColor || '#e0a422';
    /* The full review design lives in assets/hp-reviews.css. Rather than keep a
       hand-minified second copy in here — which drifted out of step with the
       stylesheet and hid the widget's heading unconditionally, leaving sections
       with no heading at all — pull the real stylesheet in by @import and add
       only what genuinely cannot be static: the star colour, which is a section
       setting. @import resolves inside a shadow root, so the imported rules
       reach the app's markup wherever it mounts. */
    var href = '';
    var link = document.querySelector('link[href*="hp-reviews.css"]');
    if (link) href = link.getAttribute('href');
    return (href ? '@import url("' + href + '");' : '') +
      '.jdgm-star,.jdgm-star.jdgm--on,.jdgm-star.jdgm--half{color:' + gold + '!important}' +
      '.jdgm-star.jdgm--off{color:' + gold + '4d!important}';
  }

  // collect a node plus every open shadow root beneath it
  function collectRoots(node, out, depth) {
    out = out || [];
    depth = depth || 0;
    if (!node || depth > 6) return out;
    out.push(node);
    var all = node.querySelectorAll ? node.querySelectorAll('*') : [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) collectRoots(all[i].shadowRoot, out, depth + 1);
    }
    return out;
  }

  /* ---- Reviews: auto-scrolling showcase rail ------------------------------
     The showcase rail drifts on its own so the section reads as a marquee, the
     way the hand-built video-review carousel does.

     It animates scrollLeft rather than cloning the cards into a seamless loop.
     Clones of app-rendered markup are inert — a cloned photo card would not
     open Judge.me's lightbox — so the rail ping-pongs between its two ends
     instead. Every card stays the real one the app rendered, and the rail is
     still an ordinary scroller: drag, wheel, keyboard and touch all work, and
     any of them stops the drift so it never fights the reader.
  ---------------------------------------------------------------------- */
  /* ---- Store-wide reviews from Judge.me's shop metafield ------------------
     The metafield is server-rendered HTML meant to be finished off by the
     widget script. Our sections print it directly — no widget mounts on it —
     so two things the script would normally do have to happen here:

       · photos ship as data-src placeholders, so nothing loads without help
       · timestamps ship as a data-content string behind a spinner class

     Both are done from the markup the app already gave us; nothing is invented.
  ---------------------------------------------------------------------- */
  function initStoreReviews(root) {
    (root || document).querySelectorAll('[data-hp-store-reviews]').forEach(function (list) {
      if (list.dataset.hpStoreWired) return;
      list.dataset.hpStoreWired = '1';

      list.querySelectorAll('.jdgm-rev__pic-img[data-src]').forEach(function (img) {
        var link = img.closest('.jdgm-rev__pic-link');
        /* data-src is the 160px thumbnail the stock widget shows inline. These
           cards render the photo full-bleed, so prefer the link's own href,
           which is the same image at w=1024. */
        var full = link && link.getAttribute('href');
        img.src = (full && full.indexOf('judgeme') !== -1) ? full : img.getAttribute('data-src');
        img.removeAttribute('data-src');
        img.loading = 'lazy';
        function done() { if (link) link.classList.remove('jdgm--loading'); }
        if (img.complete) done();
        img.addEventListener('load', done);
        // a dead image URL must not leave the card stuck behind a spinner
        img.addEventListener('error', function () {
          done();
          var pics = img.closest('.jdgm-rev__pics');
          if (pics) pics.remove();
        });
      });

      list.querySelectorAll('.jdgm-rev__timestamp[data-content]').forEach(function (el) {
        var raw = el.getAttribute('data-content');
        // "2026-05-19 11:35:35 UTC" is not a format Safari will parse as-is
        var iso = raw.replace(' UTC', 'Z').replace(' ', 'T');
        var d = new Date(iso);
        if (isNaN(d.getTime())) return;
        el.textContent = d.toLocaleDateString(undefined, {
          year: 'numeric', month: 'short', day: 'numeric'
        });
        el.classList.remove('jdgm-spinner');
      });

      // a review whose body is empty would otherwise render as a blank card
      list.querySelectorAll('.jdgm-rev').forEach(function (rev) {
        var body = rev.querySelector('.jdgm-rev__body');
        var hasPic = rev.querySelector('.jdgm-rev__pic-link');
        if (!hasPic && body && !body.textContent.trim()) rev.remove();
      });

      /* Rails are a showcase-only device. On the default card style the list
         must stay exactly as Judge.me built it, or the app's own paging and
         expand controls end up inside a horizontally scrolling row. */
      if (list.closest('.hp-revs--showcase')) {
        linkStoreCards(list);
        dealReviewRows(list);
      }
    });
  }

  /* ---- the whole card is the link ---------------------------------------
     Judge.me makes only the product name clickable, which is a small target
     in the corner of a card that otherwise looks like one. A stretched anchor
     over the card sends the whole thing to that product's reviews.

     A real <a> rather than a click handler, so it keeps what a link gives
     you: middle-click, open in a new tab, the status bar preview and keyboard
     focus. The destination is the app's own product URL — the theme never
     invents one — plus the anchor the product template's review section
     carries, so the page opens at the reviews rather than at the top.
  ---------------------------------------------------------------------- */
  function linkStoreCards(list) {
    list.querySelectorAll('.jdgm-rev').forEach(function (rev) {
      if (rev.dataset.hpLinked) return;
      var prod = rev.querySelector('.jdgm-rev__prod-link');
      var href = prod && prod.getAttribute('href');
      if (!href) return;                       // nothing to point at
      rev.dataset.hpLinked = '1';

      var overlay = document.createElement('a');
      overlay.className = 'hp-rev__hit';
      overlay.href = href.split('#')[0] + '#product-reviews';
      var name = prod.textContent.trim();
      overlay.setAttribute('aria-label', name ? 'Read reviews of ' + name : 'Read the reviews');
      rev.appendChild(overlay);
    });
  }

  /* Lay a review list's cards out as one drifting rail. Shared by the
     store-wide metafield markup (.jdgm-rev cards) and the live product widget
     (.jm-review-item cards) — both use .jdgm-review-list as their outer
     wrapper — so a product page's reviews get the same treatment as the
     store-wide sections.

     One row rather than two: with the cards a uniform width and tall enough
     to hold a whole review, a second row doubles the section's height for no
     more information, and the two counter-drifting rows made the page harder
     to read rather than livelier. */
  function dealReviewRows(list) {
    if (!list) return;
    /* Loose cards sitting directly on the list are the signal that there is
       work to do. Judge.me replaces the list's contents when the reader
       changes page, sort or filter, which wipes the rows and leaves the new
       page's cards loose again — so this is driven off the live DOM rather
       than a one-shot "already done" flag, which would have left every page
       after the first stacked and unstyled. */
    var cards = [];
    Array.prototype.forEach.call(list.children, function (c) {
      if (c.matches('.jdgm-rev, .jm-review-item')) cards.push(c);
    });
    if (!cards.length) return;

    // drop the previous page's now-empty rails before dealing the new cards
    Array.prototype.forEach.call(list.querySelectorAll('.hp-revs__row'), function (r) {
      if (!r.querySelector('.jdgm-rev, .jm-review-item')) r.remove();
    });

    function makeRow(dir) {
      var row = document.createElement('div');
      row.className = 'hp-revs__row';
      row.setAttribute('data-hp-rail', dir);
      list.appendChild(row);
      return row;
    }

    var row = makeRow('forward');
    cards.forEach(function (c) { row.appendChild(c); });
    /* hp-revs__store--rows carries the card-sizing rules (assumes the
       store-only .hp-revs__store class alongside it); hp-revs__railed is the
       list-level layout switch (block instead of the single-rail flex row)
       and is the one selector in hp-reviews.css that matches on its own, so
       it is what makes this apply to the product widget's list too, which
       never carries .hp-revs__store. */
    list.classList.add('hp-revs__store--rows', 'hp-revs__railed');
  }

  /* ---- Short review quotes get centred -----------------------------------
     Reviews run from two words to a paragraph, and the cards in a row are all
     the height of the tallest. A short one printed at the top-left of a full
     height card leaves a block of dead teal beneath it.

     The vertical centring is pure CSS (see .jm-review-content in
     hp-reviews.css) and costs nothing on a long review, which fills the space
     anyway. Deciding whether to centre the text HORIZONTALLY needs a
     measurement CSS cannot make — how many lines the quote actually ran to at
     this width — so it is done here and published as a class.
  ---------------------------------------------------------------------- */
  var HP_REV_SHORT_LINES = 3;

  function fitReviewQuotes(root) {
    /* Three markup generations, because Judge.me has shipped three and which
       one appears depends on the widget and the app block in use:
         .jdgm-carousel-item   the "Cards carousel" app block  <- what the
                               store renders on the home page
         .jm-review-item       the revamped review widget
         .jdgm-rev             the original review list
       The carousel one is NOT inside a .hp-revs section, so it is matched
       unscoped. */
    var cards = (root || document).querySelectorAll(
      '.jdgm-carousel-item, .hp-revs--showcase .jm-review-item, .hp-revs--showcase .hp-revs__store .jdgm-rev'
    );
    cards.forEach(function (card) {
      var body = card.querySelector(
        '.jdgm-text p, .jm-review-content__body, .jdgm-review-content__body-content, .jdgm-rev__body'
      );
      if (!body) { card.classList.remove('hp-rev--short'); return; }

      /* scrollHeight rather than the box height: these quotes are
         -webkit-line-clamp boxes, so a long review that has been clipped still
         reports its full height here and is correctly judged long. */
      var cs = window.getComputedStyle(body);
      var lh = parseFloat(cs.lineHeight);
      if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.55;   // 'normal'
      var lines = lh > 0 ? Math.round(body.scrollHeight / lh) : 0;

      card.classList.toggle('hp-rev--short', lines > 0 && lines <= HP_REV_SHORT_LINES);
    });
  }

  /* The carousel is injected by the Judge.me app, arrives after the theme's
     own boot, and clones its cards as it rotates. So: measure now, keep
     looking until it appears, and re-measure whenever it rebuilds. */
  function initReviewCardFit(root) {
    var scope = root || document;
    fitReviewQuotes(scope);

    var tries = 0;
    (function poll() {
      var hosts = document.querySelectorAll('.jdgm-cards-carousel, .hp-revs--showcase');
      if (!hosts.length) {
        if (tries++ < 40) window.setTimeout(poll, 250);
        return;
      }
      hosts.forEach(function (host) {
        fitReviewQuotes(host);
        if (host.dataset.hpFitObserved) return;
        host.dataset.hpFitObserved = '1';
        if (!window.MutationObserver) return;
        var t = null;
        new window.MutationObserver(function () {
          window.clearTimeout(t);
          t = window.setTimeout(function () { fitReviewQuotes(host); }, 120);
        }).observe(host, { childList: true, subtree: true });
      });
      // cards can still be arriving after the container exists
      if (tries++ < 40) window.setTimeout(poll, 250);
    })();
  }

  /* Line count is a function of card width, so a rotation or a resized window
     can turn a short quote into a long one and back. */
  var hpRevFitTimer = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(hpRevFitTimer);
    hpRevFitTimer = window.setTimeout(function () { fitReviewQuotes(document); }, 180);
  }, { passive: true });

  function initReviewMarquee(root) {
    (root || document).querySelectorAll('.hp-revs--showcase').forEach(function (section) {
      var tries = 0;

      /* Judge.me re-renders its list on page, sort and filter changes, long
         after the initial poll has given up. Re-run the deal and the
         fits/drift measurement whenever it does, so a reader who pages
         through reviews keeps the rowed design instead of dropping back to
         the app's raw stacked list. */
      if (window.MutationObserver && !section.dataset.hpRevsObserved) {
        section.dataset.hpRevsObserved = '1';
        var rerun = null;
        new window.MutationObserver(function () {
          var l = section.querySelector('.jdgm-review-list');
          // only react to the app dropping fresh, undealt cards on the list
          if (!l || !l.querySelector(':scope > .jdgm-rev, :scope > .jm-review-item')) return;
          window.clearTimeout(rerun);
          rerun = window.setTimeout(function () { tries = 0; wait(); }, 120);
        }).observe(section, { childList: true, subtree: true });
      }

      function wait() {
        /* The store-wide markup is already dealt into rows by initStoreReviews.
           The product widget hydrates asynchronously, so deal it here instead,
           once its cards exist — dealReviewRows no-ops while there is nothing
           to deal yet, and again once it has already run. */
        var list = section.querySelector('.jdgm-review-list');
        if (list) dealReviewRows(list);

        /* Re-measured on every pass, not once: cards arrive progressively and
           the web fonts settle after them, and both change how many lines a
           quote runs to. */
        fitReviewQuotes(section);

        var rails = section.querySelectorAll('[data-hp-rail]');
        if (!rails.length) {
          rails = list ? [list] : [];
        }
        if (!rails.length) {
          if (tries++ < 60) return window.setTimeout(wait, 250);
          return;
        }

        /* A rail only drifts when its cards overrun it. One that fits must be
           centred instead — left-aligned, a short row strands its cards
           against the left edge with the dead space that made the product
           page look broken. Measured rather than counted, because the card
           count that fills a rail depends on the viewport. */
        var overflowing = [];
        Array.prototype.forEach.call(rails, function (r) {
          var fits = r.scrollWidth <= r.clientWidth + 4;
          r.classList.toggle('hp-revs__row--fits', fits);
          if (!fits) overflowing.push(r);
        });

        /* Cards arrive progressively and images settle after them, so an
           early measurement can read as "fits" while more are still coming.
           Keep polling until something overflows, then arm only those; if
           nothing ever does, the centred layout above is the finished state. */
        if (!overflowing.length) {
          if (tries++ < 60) return window.setTimeout(wait, 250);
          return;
        }
        overflowing.forEach(function (rail) { armRail(rail); });
      }

      wait();
    });
  }

  function armRail(rail) {
    if (rail.dataset.hpMarquee) return;
    rail.dataset.hpMarquee = '1';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var SPEED = 0.3;             // px per frame — a slow drift, not a slide
    var reverse = rail.getAttribute('data-hp-rail') === 'reverse';
    var dir = reverse ? -1 : 1;
    // the reverse rail starts at the far end so the two rows are never in step
    if (reverse) rail.scrollLeft = rail.scrollWidth - rail.clientWidth;

    var paused = false;
    var raf = null;
    var settle;

    function step() {
      if (!paused && document.visibilityState === 'visible') {
        var max = rail.scrollWidth - rail.clientWidth;
        if (max <= 0) { raf = window.requestAnimationFrame(step); return; }
        var next = rail.scrollLeft + SPEED * dir;
        if (next >= max) { next = max; dir = -1; }
        else if (next <= 0) { next = 0; dir = 1; }
        rail.scrollLeft = next;
      }
      raf = window.requestAnimationFrame(step);
    }

    function pause() { paused = true; }
    function resume() { paused = false; }

    /* Hover deliberately does NOT pause: the rail is meant to keep drifting
       while it is read. Only a real interaction stops it — a touch or drag, a
       wheel, or keyboard focus, where a card scrolling out from under the
       focus ring would strand the reader. */
    ['focusin', 'touchstart', 'pointerdown'].forEach(function (ev) {
      rail.addEventListener(ev, pause, { passive: true });
    });
    rail.addEventListener('focusout', resume, { passive: true });
    ['touchend', 'pointerup', 'pointercancel'].forEach(function (ev) {
      rail.addEventListener(ev, function () {
        window.clearTimeout(settle);
        settle = window.setTimeout(resume, 2000);
      }, { passive: true });
    });
    // a manual scroll should hand control back only once the reader stops
    rail.addEventListener('wheel', function () {
      pause();
      window.clearTimeout(settle);
      settle = window.setTimeout(resume, 2000);
    }, { passive: true });

    raf = window.requestAnimationFrame(step);

    // Judge.me re-renders its list on sort, filter and page change, which
    // replaces these nodes. Stop animating one that has left the document.
    var host = rail.parentNode;
    if (host && window.MutationObserver) {
      new window.MutationObserver(function () {
        if (!document.contains(rail) && raf) window.cancelAnimationFrame(raf);
      }).observe(host, { childList: true, subtree: true });
    }
  }

  function initReviewPager(root) {
    (root || document).querySelectorAll('[data-hp-reviews-pager]').forEach(function (box) {
      if (box.dataset.hpPagerWired) return;

      var perPage = parseInt(box.getAttribute('data-hp-reviews-pager'), 10) || 2;
      var star = box.getAttribute('data-hp-star') || '#e0a422';
      var tries = 0;

      (function wait() {
        // look in the block and in any shadow roots it contains
        var roots = collectRoots(box, []);
        var host = null;
        var items = [];
        for (var i = 0; i < roots.length; i++) {
          var found = roots[i].querySelectorAll('.jdgm-rev, [data-review-id]');
          if (found.length) { host = roots[i]; items = Array.prototype.slice.call(found); break; }
        }

        // style every root we can reach, even before reviews resolve
        roots.forEach(function (r) {
          if (r.__hpStyled) return;
          r.__hpStyled = true;
          // document-level styles already come from the section stylesheet
          if (r === document || r.nodeType === 1) return;
          try {
            var st = document.createElement('style');
            st.textContent = hpReviewCss(star);
            r.appendChild(st);
          } catch (e) {}
        });

        if (!items.length) {
          if (tries++ < 40) return window.setTimeout(wait, 250);
          return;   // app rendered something we do not recognise — leave it alone
        }
        box.dataset.hpPagerWired = '1';
        if (items.length <= perPage) return;

        // silence the app's own paginator
        (host.querySelectorAll ? host.querySelectorAll('.jdgm-paginate, .jdgm-rev-widg__paginate') : [])
          .forEach(function (n) { n.style.display = 'none'; });

        var pages = Math.ceil(items.length / perPage);
        var page = 0;

        var nav = document.createElement('div');
        nav.className = 'hp-revpager';
        nav.innerHTML =
          '<button type="button" class="hp-revpager__btn" data-prev aria-label="Previous reviews">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>' +
          '<span class="hp-revpager__label" data-label></span>' +
          '<button type="button" class="hp-revpager__btn" data-next aria-label="More reviews">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>';

        var list = items[0].parentNode;
        if (list && list.parentNode) list.parentNode.insertBefore(nav, list.nextSibling);
        else box.appendChild(nav);

        var prev = nav.querySelector('[data-prev]');
        var next = nav.querySelector('[data-next]');
        var label = nav.querySelector('[data-label]');

        function render(scroll) {
          items.forEach(function (el, i) {
            el.style.display = (i >= page * perPage && i < (page + 1) * perPage) ? '' : 'none';
          });
          label.textContent = 'Page ' + (page + 1) + ' of ' + pages;
          prev.disabled = page === 0;
          next.disabled = page === pages - 1;
          if (scroll) {
            var top = box.getBoundingClientRect().top + window.scrollY - 120;
            window.scrollTo({ top: top, behavior: reducedMotion ? 'auto' : 'smooth' });
          }
          if (hasGsap) window.ScrollTrigger.refresh();
        }

        prev.addEventListener('click', function () { if (page > 0) { page--; render(true); } });
        next.addEventListener('click', function () { if (page < pages - 1) { page++; render(true); } });
        render(false);
      })();
    });
  }

  /* ---- Background video (About hero) ------------------------------------
     The poster image is the real content; the video only fades in once it can
     actually play, so a blocked autoplay or a slow connection leaves the still
     photograph in place instead of a black rectangle.
  ---------------------------------------------------------------------- */
  /* Should this screen fetch a background clip at all?

     On a phone the hero clip is by far the heaviest thing on the page, and it
     buys nothing on a connection that cannot deliver it before the visitor has
     scrolled past. The still is real content, not a placeholder, so declining
     the video costs the design very little and saves megabytes. Three reasons
     to decline: the merchant turned it off for phones, the visitor asked for
     less data, or the connection reports itself as slow. */
  /* One breakpoint, one answer. Every decision below asks this same function,
     so "is this a phone?" cannot come out differently in the check that gates
     the download, the one that picks the file, and the one that schedules the
     fetch. It matches the 749px query the hero's CSS uses for the poster. */
  var HP_PHONE_MQ = '(max-width: 749px)';
  function hpIsPhone() { return window.matchMedia(HP_PHONE_MQ).matches; }

  /* The <source> children Shopify's video_tag emits ARE the desktop cut, and a
     <video> that still owns them keeps them as a candidate the browser may fall
     back to on its own. Take them out of the element and hold on to them, so
     that once a file has been chosen the src attribute is the only candidate
     there is — on a phone, nothing can quietly reinstate the landscape clip. */
  function hpStashSources(v) {
    if (!v.hpSources) {
      v.hpSources = Array.prototype.slice.call(v.querySelectorAll('source'));
    }
    v.hpSources.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); });
  }
  function hpRestoreSources(v) {
    if (!v.hpSources) return;
    v.hpSources.forEach(function (n) { if (!n.parentNode) v.appendChild(n); });
  }
  /* Put the element back to holding no file at all — used when this screen has
     decided it wants no clip, so the still image is what shows and there is
     nothing left for the browser to start. */
  function hpClearVideo(v) {
    hpStashSources(v);
    v.autoplay = false;
    v.removeAttribute('autoplay');
    try { v.pause(); } catch (e) {}
    if (v.getAttribute('src') !== null) { v.removeAttribute('src'); v.load(); }
    v.classList.remove('is-playing');
  }

  /* Which file this screen should get.

     The rule that matters: the markup's <source> children are a DESKTOP
     candidate and only ever a desktop one. Treating them as a generic fallback
     is what let the landscape clip through on phones. A phone takes the phone
     cut, and reaches for a desktop file only when no phone cut was authored at
     all — an authored phone clip always wins on a phone, and never appears on a
     wide screen. */
  function hpPickSource(host, v, phone) {
    var desktopSrc = host.getAttribute('data-src-desktop') || '';
    var mobileSrc = host.getAttribute('data-src-mobile') || '';
    var hasSources = !!(v.hpSources && v.hpSources.length);
    var markupSrc = v.hpMarkupSrc || '';

    if (phone) {
      if (mobileSrc) return { src: mobileSrc };
      if (desktopSrc) return { src: desktopSrc };
      if (hasSources) return { sources: true };
      return markupSrc ? { src: markupSrc } : null;
    }
    if (desktopSrc) return { src: desktopSrc };
    if (hasSources) return { sources: true };
    if (markupSrc) return { src: markupSrc };
    return mobileSrc ? { src: mobileSrc } : null;
  }

  function shouldLoadBgVideo(host) {
    if (!hpIsPhone()) return true;

    var allowed = host.getAttribute('data-hp-video-mobile');
    if (allowed === 'false') return false;

    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c) {
      if (c.saveData) return false;
      var t = c.effectiveType || '';
      if (t === 'slow-2g' || t === '2g' || t === '3g') return false;
    }
    return true;
  }

  /* Start the fetch, but not while the page is still painting. A phone that
     begins downloading the clip during load is competing with the images and
     fonts the first screen actually needs; waiting for load and then for an
     idle moment moves it out of that contention entirely. Desktop starts
     immediately, where the bandwidth is not the constraint.

     The hold on top of that is what pays for the clip being full quality
     again. Nothing is missing while it runs: the still is the frame the video
     opens on, painted as the stage's own background, so a longer hold is a
     longer look at a photograph rather than an empty box — and every byte of
     the clip is spent after the page has settled instead of against it.

     Read off the section so it is tunable from the theme editor; the fallback
     applies to any stage that predates the attribute. */
  var HP_VIDEO_HOLD_MS = 2000;

  function whenIdleForVideo(fn, host) {
    if (!hpIsPhone()) return fn();

    var hold = parseInt(host && host.getAttribute('data-hp-video-hold'), 10);
    if (!(hold >= 0)) hold = HP_VIDEO_HOLD_MS;

    var go = function () {
      window.setTimeout(function () {
        if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2500 });
        else fn();
      }, hold);
    };
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }

  function initBgVideo(root) {
    (root || document).querySelectorAll('[data-hp-bg-video]').forEach(function (host) {
      if (host.dataset.hpVidDone) return;
      host.dataset.hpVidDone = '1';

      /* The marker sits on the <video> when the markup builds the tag itself,
         and on the wrapper when Shopify's video_tag built it (that filter takes
         no data attributes). Reach through either way. */
      var v = host.tagName === 'VIDEO' ? host : host.querySelector('video');
      if (!v) return;

      /* Record what the markup shipped, then take its <source> children away,
         before any decision and on every screen. From here on this element
         holds exactly the one file we hand it and nothing else. */
      v.hpMarkupSrc = v.getAttribute('src') || '';
      hpStashSources(v);
      v.muted = true;

      var show = function () { v.classList.add('is-playing'); };
      v.addEventListener('playing', show);

      var attempt = function () {
        var p = v.play();
        if (p && p.catch) p.catch(function () { /* the still image stays */ });
      };

      /* 'auto', not 'metadata': the markup ships with no autoplay or preload of
         its own (see hp-hero.liquid) precisely so nothing downloads until this
         point has chosen the file. This is the one fetch that should happen, so
         it asks for the whole thing. autoplay is set as a PROPERTY, now that the
         right file is in place, so the browser starts the clip itself as soon as
         it can — play() alone, called in the same turn as load(), rejects while
         the element is still restarting resource selection. */
      var start = function () {
        v.preload = 'auto';
        v.autoplay = true;
        v.load();
        v.addEventListener('loadeddata', attempt, { once: true });
        v.addEventListener('canplay', attempt, { once: true });
        if (v.readyState >= 3) { show(); attempt(); }
      };

      // What the element is currently set to: a URL, 'sources', or null.
      var current = null;

      var apply = function () {
        if (reducedMotion || !shouldLoadBgVideo(host)) {
          /* Also covers the very first pass, where the element may still be
             holding the desktop URL the markup shipped: strip it, so a phone
             that has declined the video has nothing left that could start. */
          if (current !== null || v.getAttribute('src') !== null) {
            hpClearVideo(v);
            current = null;
          }
          return;                                  // the still image stands alone
        }

        var pick = hpPickSource(host, v, hpIsPhone());
        if (!pick) return;

        if (pick.sources) {
          if (current === 'sources') return;
          current = 'sources';
          v.removeAttribute('src');
          hpRestoreSources(v);
          start();
          return;
        }

        if (current === pick.src) return;
        current = pick.src;
        hpStashSources(v);          // the src attribute is the only candidate

        /* The file the settings named, at the quality they named it — on every
           screen. Phones used to be handed a 480p re-encode of this same clip
           to save about a megabyte, and the saving was real, but so was the
           softness: the hero is the first thing anyone sees and it looked it.
           The bytes are bought back by holding the still image longer instead
           (see whenIdleForVideo) rather than by degrading the picture.

           Still a queue of one, because the error handler below walks it. */
        var queue = [pick.src];
        v.hpQueue = queue;
        v.hpQueueAt = 0;
        v.setAttribute('src', queue[0]);
        start();
      };

      /* A candidate that does not exist comes back as a media error rather than
         a failed fetch, because the URL is on the element rather than in a
         fetch() we could inspect. Step to the next one and start again; when
         the list runs out, stop and leave the still image showing. */
      v.addEventListener('error', function () {
        /* Clearing the element (a phone that declined the clip) empties it and
           can surface as an error too. No src means nothing to fall back to. */
        if (v.getAttribute('src') === null) return;
        var queue = v.hpQueue;
        if (!queue || v.hpQueueAt >= queue.length - 1) return;
        v.hpQueueAt += 1;
        current = queue[v.hpQueueAt];
        v.setAttribute('src', current);
        start();
      });

      /* Once the hero has scrolled off, the clip is decoding frames nobody is
         looking at and pulling the remainder of a file nobody needs yet — on a
         phone that is the main thread and the connection both, spent on an
         empty room. Pause on the way out, resume on the way back in.

         Guarded on the attribute so this only applies where the markup asked
         for it, and skipped entirely where IntersectionObserver is missing:
         the clip simply keeps playing, as it did before. */
      if (host.hasAttribute('data-hp-video-pause-offscreen') && window.IntersectionObserver) {
        var seen = true;
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting === seen) return;
            seen = entry.isIntersecting;
            if (!v.getAttribute('src')) return;
            if (seen) {
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              try { v.pause(); } catch (e) {}
            }
          });
        }, { rootMargin: '100px' }).observe(host);
      }

      whenIdleForVideo(apply, host);

      /* Re-run when the screen crosses the breakpoint — a phone rotated into
         landscape, a desktop window dragged narrow, the theme editor switching
         device preview. Without this the element keeps whichever cut it was
         handed on first load, which is the second way to end up showing the
         wrong one. Re-entering apply() is cheap: it returns immediately unless
         the file for this screen has actually changed. */
      var mq = window.matchMedia(HP_PHONE_MQ);
      if (mq.addEventListener) mq.addEventListener('change', apply);
      else if (mq.addListener) mq.addListener(apply);
    });
  }

  /* ---- Pinch-zoom / drag-pan on product photos (touch) --------------------
     Dawn's own zoom is magnify.js, wired to onmousemove and onmouseleave. A
     phone fires neither, so tapping a product photo opened the lightbox and
     that was the end of it — a picture at exactly screen size, no way in.

     This adds the two gestures people actually try on a phone: pinch to zoom
     and drag to pan, plus double-tap as the quick way in and out. It runs only
     on a coarse pointer, so a mouse keeps the stock hover/lightbox behaviour
     exactly as it was.

     Geometry note: everything is done in screen coordinates against
     getBoundingClientRect, with transform-origin at the top-left (set in CSS).
     That origin is what makes it tractable — scaling from 0 0 leaves the
     element's left/top where they are, so its on-screen box is simply
     base + translate, and `base` can be recovered as rect.left - tx at any
     moment without tracking layout separately.
  ---------------------------------------------------------------------- */
  var HP_ZOOM_MAX = 4;
  var HP_ZOOM_TAP = 2.5;      // where a double-tap lands

  function initProductZoom(root) {
    /* Touch only. A desktop mouse has the hover magnifier and the lightbox
       already, and taking over its gestures would be a regression. */
    if (!window.matchMedia('(pointer: coarse)').matches) return;

    (root || document).querySelectorAll('product-modal').forEach(function (modal) {
      if (modal.dataset.hpZoomDone) return;
      modal.dataset.hpZoomDone = '1';

      var content = modal.querySelector('.product-media-modal__content');
      if (!content) return;
      var dialog = modal.querySelector('.product-media-modal__dialog') || modal;

      var img = null;             // the image this gesture belongs to
      var scale = 1, tx = 0, ty = 0;
      var base = null;            // untransformed screen box of img
      var startScale = 1, startTx = 0, startTy = 0;
      var startDist = 0, startMid = null, panFrom = null;
      var downAt = 0, downX = 0, downY = 0, moved = false;
      var lastTapAt = 0, lastTapX = 0, lastTapY = 0;
      var canFull = true;

      /* Can the photo be let out of its box when zoomed? Only if that box is
         not doing a job of its own — on a tablet the lightbox stacks every
         image and scrolls, and switching a scrolled container to
         overflow: visible would throw its scroll position away.

         Measured only while nothing is zoomed. A transformed image overflows
         its container, so scrollHeight reads as scrollable the moment a zoom
         starts, and asking mid-gesture would always answer "no". */
      function measureScrollable() {
        canFull = content.scrollHeight <= content.clientHeight + 1;
      }

      /* What the pan is allowed to move within. At rest that is the photo's own
         box; zoomed, it is the whole lightbox, so every edge of the picture can
         be brought to every edge of the screen. */
      function frameRect() {
        return (canFull && scale > 1.01) ? dialog.getBoundingClientRect()
                                         : content.getBoundingClientRect();
      }

      function apply() {
        if (!img) return;
        img.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) scale(' + scale + ')';
        img.classList.toggle('is-zoomed', scale > 1.01);
        content.classList.toggle('hp-zoom-full', canFull && scale > 1.01);
      }

      /* Where the image would sit with no transform at all, in screen
         coordinates. transform-origin is 0 0, so a transformed element's
         on-screen left is simply base.left + tx — which inverts to give base
         from a single measurement.

         This MUST be taken while the DOM still matches tx/ty/scale, i.e. at
         the start of a gesture and never mid-calculation. Measuring it after
         the new values had been assigned but before they were written out was
         a real bug: getBoundingClientRect returned the OLD transform's box
         while the arithmetic used the NEW translate, so every clamp was off by
         a whole gesture and a pinch threw the photo a thousand pixels off
         screen. Cache it once per gesture and the arithmetic stays honest. */
      function measureBase() {
        if (!img) return;
        var r = img.getBoundingClientRect();
        base = { left: r.left - tx, top: r.top - ty, w: img.offsetWidth, h: img.offsetHeight };
      }

      /* Keep the image inside the frame: centred when it is smaller than the
         frame, and never pulled far enough for a strip of background to appear
         beside it when it is bigger. */
      function clampPan() {
        if (!img || !base) return;
        var cr = frameRect();
        var w = base.w * scale;
        var h = base.h * scale;

        if (w <= cr.width) tx = cr.left + (cr.width - w) / 2 - base.left;
        else tx = Math.min(cr.left - base.left, Math.max(cr.right - w - base.left, tx));

        if (h <= cr.height) ty = cr.top + (cr.height - h) / 2 - base.top;
        else ty = Math.min(cr.top - base.top, Math.max(cr.bottom - h - base.top, ty));
      }

      /* Rescale about a fixed screen point, so the spot between the fingers
         (or under the double-tap) stays put instead of the image jumping to
         centre. */
      function scaleAround(next, px, py) {
        if (!base) measureBase();
        next = Math.min(HP_ZOOM_MAX, Math.max(1, next));
        var ix = (px - base.left - tx) / scale;      // the point, in image space
        var iy = (py - base.top - ty) / scale;
        scale = next;
        tx = px - base.left - ix * scale;
        ty = py - base.top - iy * scale;
        clampPan();
      }

      function animated(fn) {
        if (!img) return;
        var el = img;
        el.classList.add('is-animating');
        fn();
        apply();
        window.setTimeout(function () { el.classList.remove('is-animating'); }, 320);
      }

      function rest() {
        scale = 1; tx = 0; ty = 0;
        clampPan();
      }

      function reset(el) {
        if (!el) return;
        el.style.transform = '';
        el.classList.remove('is-zoomed', 'is-animating');
      }

      function resetAll() {
        content.querySelectorAll('.hp-zoomable').forEach(reset);
        content.classList.remove('hp-zoom-full');
        img = null; base = null; scale = 1; tx = 0; ty = 0;
      }

      function imageFrom(target) {
        var el = target && target.closest ? target.closest('img') : null;
        return el && content.contains(el) && el.classList.contains('hp-zoomable') ? el : null;
      }

      function dist(a, b) { return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY); }
      function mid(a, b) {
        return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
      }

      /* A photo the visitor has never touched starts at rest rather than
         inheriting whatever the last one was left at. */
      function adopt(el) {
        if (img === el) return;
        img = el;
        if (!el.style.transform) { scale = 1; tx = 0; ty = 0; }
      }

      content.addEventListener('touchstart', function (e) {
        var el = imageFrom(e.target);
        if (!el) return;
        adopt(el);
        img.classList.remove('is-animating');
        if (scale <= 1.01) measureScrollable();
        measureBase();

        if (e.touches.length === 2) {
          startDist = dist(e.touches[0], e.touches[1]);
          startMid = mid(e.touches[0], e.touches[1]);
          startScale = scale;
          startTx = tx; startTy = ty;
          panFrom = null;
          e.preventDefault();
          return;
        }

        if (e.touches.length === 1) {
          var t = e.touches[0];
          downAt = Date.now(); downX = t.clientX; downY = t.clientY; moved = false;

          /* Only claim the drag once zoomed. At rest the lightbox still needs
             to scroll normally under the finger. */
          if (scale > 1.01) {
            panFrom = { x: t.clientX, y: t.clientY };
            startTx = tx; startTy = ty;
          }
        }
      }, { passive: false });

      content.addEventListener('touchmove', function (e) {
        if (!img) return;

        if (e.touches.length === 2 && startDist > 0) {
          e.preventDefault();
          moved = true;
          var d = dist(e.touches[0], e.touches[1]);
          var m = mid(e.touches[0], e.touches[1]);
          /* Rebuild from the gesture's starting state each frame rather than
             accumulating, so rounding cannot drift over a long pinch. */
          scale = startScale; tx = startTx; ty = startTy;
          scaleAround(startScale * (d / startDist), startMid.x, startMid.y);
          tx += m.x - startMid.x;          // follow the fingers as they travel
          ty += m.y - startMid.y;
          clampPan();
          apply();
          return;
        }

        if (e.touches.length === 1) {
          var t = e.touches[0];
          if (Math.abs(t.clientX - downX) > 10 || Math.abs(t.clientY - downY) > 10) moved = true;
          if (!panFrom) return;
          e.preventDefault();
          tx = startTx + (t.clientX - panFrom.x);
          ty = startTy + (t.clientY - panFrom.y);
          clampPan();
          apply();
        }
      }, { passive: false });

      function end(e) {
        if (!img) return;
        var left = e.touches ? e.touches.length : 0;

        if (left === 1 && startDist > 0) {
          /* One finger lifted mid-pinch: hand over to a pan from where that
             finger now is, rather than letting the image jump. */
          startDist = 0;
          measureBase();
          panFrom = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          startTx = tx; startTy = ty;
          moved = true;
          return;
        }
        if (left > 0) return;

        var wasPinch = startDist > 0;
        startDist = 0;
        panFrom = null;

        /* A tap is a touch that went down and came up in the same place,
           quickly. Testing that here rather than on touchstart is what keeps a
           pan from being read as a tap: three quick drags from the same spot
           used to register as double-taps and yank the zoom around
           mid-gesture. */
        var now = Date.now();
        var isTap = !wasPinch && !moved && (now - downAt) < 250;
        if (isTap) {
          if (now - lastTapAt < 320 &&
              Math.abs(downX - lastTapX) < 30 && Math.abs(downY - lastTapY) < 30) {
            lastTapAt = 0;
            measureBase();
            animated(function () {
              if (scale > 1.01) rest();
              else scaleAround(HP_ZOOM_TAP, downX, downY);
            });
            return;
          }
          lastTapAt = now; lastTapX = downX; lastTapY = downY;
          return;
        }

        // Settle back if a pinch ended below 1x.
        if (scale <= 1.01) animated(rest);
      }
      content.addEventListener('touchend', end);
      content.addEventListener('touchcancel', end);

      /* iOS Safari runs its own pinch on top of touch events and ignores
         preventDefault on those; these are the events that stop the whole page
         zooming behind the lightbox. */
      ['gesturestart', 'gesturechange', 'gestureend'].forEach(function (name) {
        content.addEventListener(name, function (ev) { ev.preventDefault(); });
      });

      /* Mark the still images as zoomable, and start every visit to the
         lightbox at rest — reopening on a different photo should not inherit
         the last one's zoom. Videos and 3D models keep their own controls. */
      function markImages() {
        content.querySelectorAll('img').forEach(function (el) {
          if (!el.closest('.deferred-media') && !el.closest('.product-media-modal__model')) {
            el.classList.add('hp-zoomable');
          }
        });
      }
      markImages();
      measureScrollable();

      new MutationObserver(function () {
        markImages();
        resetAll();
        measureScrollable();
      }).observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  }

  /* ---- Sale / deal popup --------------------------------------------------
     Driven by a "deal" block on the announcement bar section. The markup ships
     with `hidden` on it and this is what takes it off, so the popup cannot be
     on screen before the code that closes it exists.
  ---------------------------------------------------------------------- */
  var dealPopupShown = false;

  function initDealPopup(root) {
    (root || document).querySelectorAll('[data-hp-deal]').forEach(function (el) {
      if (el.dataset.hpDealDone) return;
      el.dataset.hpDealDone = '1';

      var id = el.getAttribute('data-hp-deal-id') || '';

      /* Re-parent to <body>. The block is authored inside the header group,
         and a position: fixed element is measured against the nearest ancestor
         carrying a transform, filter or containment rather than the viewport —
         several header and drawer states have one. Moving the popup out
         removes that entire class of "it is stuck inside the header" bug
         instead of trying to enumerate which ancestors cause it. */
      if (el.parentNode !== document.body) {
        /* A section re-render in the theme editor emits a fresh copy of this
           markup while the previous one is still parked on the body. Clear the
           old one first or the editor accumulates a stack of popups. */
        Array.prototype.slice.call(document.body.children).forEach(function (node) {
          if (node !== el && node.hasAttribute && node.hasAttribute('data-hp-deal') &&
              node.getAttribute('data-hp-deal-id') === id) {
            node.parentNode.removeChild(node);
          }
        });
        document.body.appendChild(el);
      }

      var panel = el.querySelector('.hp-deal__panel');
      var freq = el.getAttribute('data-frequency') || 'always';
      var delay = parseFloat(el.getAttribute('data-delay') || '2') * 1000;
      var designMode = !!(window.Shopify && window.Shopify.designMode);
      var key = 'hp-deal-' + id;
      var lastFocus = null;
      var hideTimer = null;

      /* 'Once per visit' is sessionStorage, 'once a day' is localStorage —
         each frequency stored where its lifetime already matches, so nothing
         has to expire a session by hand. Every access is wrapped: storage
         throws outright in some privacy modes, and a popup is not worth an
         exception that stops the rest of the script. */
      function store() { return freq === 'day' ? window.localStorage : window.sessionStorage; }
      function seen() {
        if (freq === 'always') return false;
        try {
          var v = store().getItem(key);
          if (!v) return false;
          if (freq === 'session') return true;
          return (Date.now() - parseInt(v, 10)) < 86400000;
        } catch (e) { return false; }
      }
      function mark() {
        if (freq === 'always') return;
        try { store().setItem(key, String(Date.now())); } catch (e) { /* nothing to do */ }
      }

      function focusables() {
        return el.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      }

      function onKey(e) {
        if (e.key === 'Escape' || e.key === 'Esc') { close(); return; }
        if (e.key !== 'Tab') return;
        /* Keep Tab inside the dialog. Without this the focus ring walks off
           into the page behind the backdrop, where nothing is clickable and
           the visitor cannot see where they are. */
        var f = focusables();
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }

      function videos() { return el.querySelectorAll('[data-hp-lazy-video]'); }

      function open() {
        if (!el.hasAttribute('hidden') && el.classList.contains('is-open')) return;
        window.clearTimeout(hideTimer);
        /* Fetch the clip now, not on page load: a popup set to "once a day"
           should cost nothing on the days it does not appear. */
        videos().forEach(function (v) {
          startLazyVideo(v);
          var p = v.play();
          if (p && p.catch) p.catch(function () { /* the still stays */ });
        });
        lastFocus = document.activeElement;
        el.removeAttribute('hidden');
        /* One frame between "displayed" and "open" so the fade actually has a
           start state to travel from; adding both together gives no
           transition at all. */
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () { el.classList.add('is-open'); });
        });
        document.documentElement.classList.add('hp-deal-open');
        document.addEventListener('keydown', onKey);
        if (panel) panel.focus();
        mark();
      }

      function close() {
        el.classList.remove('is-open');
        /* A looping clip behind a hidden element still decodes frames and still
           costs battery. Nothing can see it, so stop it. */
        videos().forEach(function (v) { try { v.pause(); } catch (e) {} });
        document.documentElement.classList.remove('hp-deal-open');
        document.removeEventListener('keydown', onKey);
        /* Wait out the fade before hiding, on a timer rather than transitionend:
           a browser that never fires the event (or fires it for a different
           property) would otherwise leave the popup displayed but invisible,
           sitting over the page and swallowing every click. */
        hideTimer = window.setTimeout(function () { el.setAttribute('hidden', ''); }, 480);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      el.querySelectorAll('[data-hp-deal-close]').forEach(function (btn) {
        btn.addEventListener('click', close);
      });
      /* A click that starts and ends on the panel must not close, even when it
         finishes over the backdrop — dragging to select text in the copy
         otherwise dismisses the popup. Only a press that began on the backdrop
         counts. */
      var backdrop = el.querySelector('.hp-deal__backdrop');
      if (backdrop) {
        var pressedOnBackdrop = false;
        backdrop.addEventListener('mousedown', function () { pressedOnBackdrop = true; });
        document.addEventListener('mouseup', function () {
          window.setTimeout(function () { pressedOnBackdrop = false; }, 0);
        });
        backdrop.addEventListener('click', function (e) {
          if (e.target === backdrop && pressedOnBackdrop) close();
        });
      }

      if (designMode) {
        /* In the editor the popup opens when the merchant selects the block and
           closes when they move on, so it is never in the way while they work
           on the rest of the page — and it never opens on its own there, which
           would make every other section impossible to edit. */
        document.addEventListener('shopify:block:select', function (e) {
          if (e.target === el || el.contains(e.target)) open();
        });
        document.addEventListener('shopify:block:deselect', function (e) {
          if (e.target === el || el.contains(e.target)) close();
        });
        return;
      }

      if (seen()) return;
      window.setTimeout(function () {
        /* Two deal blocks must not stack two popups over each other. First one
           due wins; the other stays put until the merchant reorders or hides
           one. */
        if (dealPopupShown) return;
        dealPopupShown = true;
        open();
      }, delay);
    });
  }

  /* ---- Lazy "poster" videos (review cards with no real poster image) ----
     [data-hp-lazy-video] ships with no src and preload="none" — see the
     comment in hp-video-reviews.liquid for why: these clips are not
     faststart-encoded, so preload="metadata" downloads the entire file
     instead of a small header. Fetching the real src only once a card is
     about to scroll into view keeps that cost off every page load and pays
     it only for cards someone actually reaches. */
  /* Module-level so the deal popup can start its own clip on open. A popup is
     display:none until then, which makes it invisible to the intersection
     observer below — watching it would wait forever. "The popup opened" is the
     better trigger anyway: it is the exact moment the clip becomes worth
     fetching. */
  function startLazyVideo(v) {
    if (v.dataset.hpLazyDone) return;
    v.dataset.hpLazyDone = '1';
    v.src = v.dataset.src;
    if (v.hasAttribute('data-hp-lazy-autoplay')) {
      v.loop = true;
      v.addEventListener('loadeddata', function () {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }, { once: true });
    }
    v.load();
  }

  function initLazyPreviewVideos(root) {
    var all = (root || document).querySelectorAll('[data-hp-lazy-video]');
    /* Clips inside a deal popup are owned by initDealPopup and excluded here:
       the popup is also re-parented to <body> after this runs, so a section
       this observer had already latched on to would no longer contain the
       video by the time it fired. */
    var nodes = Array.prototype.filter.call(all, function (v) {
      return !v.closest('[data-hp-deal]');
    });
    if (!nodes.length) return;

    var start = startLazyVideo;

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(start);
      return;
    }

    /* Safety net: if the observer never reports an intersection for some
       reason — an edge case in a specific browser, a section whose layout
       genuinely never overlaps the expanded root margin, anything — these
       cards must not stay permanently unloaded. Load whatever the observer
       has not already handled once the page has been open a while and the
       visitor has actually scrolled, which is a reasonable proxy for "likely
       to reach this section eventually" without paying the cost on every
       load regardless of engagement. */
    var fallbackArmed = false;
    var armFallback = function () {
      if (fallbackArmed) return;
      fallbackArmed = true;
      window.setTimeout(function () { nodes.forEach(start); }, 8000);
    };
    window.addEventListener('scroll', armFallback, { once: true, passive: true });

    /* Observe the SECTION each card lives in, not the card itself. These
       cards sit inside a horizontally-scrolling marquee (transform:
       translateX, animated), so a card's own bounding box routinely sits
       outside the viewport off to the side mid-animation even while the
       section around it is fully in view vertically — an observer watching
       the card directly can go the whole page load without ever reporting
       an intersection. The section's own box is stationary, so watching
       that instead and starting every lazy video inside it at once, the
       moment it comes into view, is what actually corresponds to "the
       marquee is visible enough to be worth paying for." */
    var sections = new Set();
    nodes.forEach(function (v) {
      var section = v.closest('.shopify-section') || v.closest('section') || v.parentElement;
      sections.add(section);
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.querySelectorAll('[data-hp-lazy-video]').forEach(start);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '600px 0px' });
    sections.forEach(function (s) { io.observe(s); });
  }

  /* ---- Count-up numbers --------------------------------------------------
     Reads the number out of whatever the merchant typed — "1,000+", "100%",
     "Rs. 8,000" — animates only the digits and keeps the prefix, suffix,
     decimals and thousands separators exactly as written. Anything without a
     number in it is left alone, so no setting can break the display.
  ---------------------------------------------------------------------- */
  function initCounters(root) {
    var nodes = (root || document).querySelectorAll('[data-hp-count]');
    if (!nodes.length) return;

    var run = function (el) {
      var raw = el.getAttribute('data-hp-count') || el.textContent;
      var match = raw.match(/\d[\d,]*(\.\d+)?/);
      if (!match) return;
      var numText = match[0];
      var prefix = raw.slice(0, match.index);
      var suffix = raw.slice(match.index + numText.length);
      var grouped = numText.indexOf(',') !== -1;
      var decimals = (numText.split('.')[1] || '').length;
      var target = parseFloat(numText.replace(/,/g, ''));
      if (isNaN(target)) return;

      var format = function (n) {
        var s = n.toFixed(decimals);
        if (grouped) {
          var parts = s.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          s = parts.join('.');
        }
        return prefix + s + suffix;
      };

      if (reducedMotion) { el.textContent = format(target); return; }

      var dur = 1400;
      var t0 = null;
      var step = function (t) {
        if (t0 === null) t0 = t;
        var p = Math.min((t - t0) / dur, 1);
        // easeOutExpo — fast start, long settle, so the final digits land softly
        var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
        el.textContent = format(target * eased);
        if (p < 1) window.requestAnimationFrame(step);
      };
      window.requestAnimationFrame(step);
    };

    var start = function (el) {
      if (el.dataset.hpCountDone) return;
      el.dataset.hpCountDone = '1';
      run(el);
    };

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(start);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        start(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.4 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---- <hp-process> — sticky factory walk -------------------------------
     Whichever step the reader is actually looking at becomes active: its
     photograph cross-fades into the sticky frame, its caption and number
     update, and the progress bar advances.

     This measures against a "reading line" — the middle of the space beside
     the sticky frame — on every scroll frame, rather than watching an
     IntersectionObserver band. The observer version fell a step behind:
     entries only fire as an element crosses the band edge, so with steps
     taller than the band two of them could be inside it at once and the
     lowest index won, leaving the photograph showing the step above the one
     being read.
  ---------------------------------------------------------------------- */
  var HpProcess = (function () {
    function HpProcess() { return Reflect.construct(HTMLElement, [], HpProcess); }
    HpProcess.prototype = Object.create(HTMLElement.prototype, { constructor: { value: HpProcess } });
    Object.setPrototypeOf(HpProcess, HTMLElement);

    HpProcess.prototype.connectedCallback = function () {
      var el = this;
      el.steps = Array.prototype.slice.call(el.querySelectorAll('[data-hp-proc-step]'));
      el.layers = Array.prototype.slice.call(el.querySelectorAll('[data-hp-proc-layer]'));
      el.numEl = el.querySelector('[data-hp-proc-num]');
      el.capEl = el.querySelector('[data-hp-proc-caption]');
      el.fillEl = el.querySelector('[data-hp-proc-fill]');
      el.frame = el.querySelector('.hp-abt-proc__stack');
      if (!el.steps.length) return;

      el.active = -1;
      el.setActive(0);

      var ticking = false;
      el.onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          el.sync();
        });
      };
      window.addEventListener('scroll', el.onScroll, { passive: true });
      window.addEventListener('resize', el.onScroll);
      // images settle after layout and shift every step's position
      window.addEventListener('load', el.onScroll);
      el.sync();
    };

    HpProcess.prototype.disconnectedCallback = function () {
      if (!this.onScroll) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('load', this.onScroll);
    };

    /* The line to measure against: where the middle of the sticky frame sits
       once pinned — taken from the sticky offset rather than the frame's
       current box, because before it pins the frame sits low in the viewport
       and would make the second step active on arrival at the section. */
    HpProcess.prototype.readingLine = function () {
      var vh = window.innerHeight;
      var media = this.frame && this.frame.parentElement;
      if (this.frame && this.frame.offsetParent && media) {
        var top = parseFloat(window.getComputedStyle(media).top);
        var h = this.frame.getBoundingClientRect().height;
        if (!isNaN(top) && h) {
          return Math.max(vh * 0.15, Math.min(top + h / 2, vh * 0.85));
        }
      }
      return vh / 2;
    };

    HpProcess.prototype.sync = function () {
      var el = this;
      var line = el.readingLine();
      var best = 0;
      var bestGap = Infinity;

      for (var i = 0; i < el.steps.length; i++) {
        var r = el.steps[i].getBoundingClientRect();
        if (line >= r.top && line <= r.bottom) { best = i; bestGap = 0; break; }
        // in the gap between two steps (or past the ends): take the nearest
        var gap = line < r.top ? r.top - line : line - r.bottom;
        if (gap < bestGap) { bestGap = gap; best = i; }
      }
      el.setActive(best);
    };

    HpProcess.prototype.setActive = function (i) {
      var el = this;
      if (el.active === i) return;
      el.active = i;

      el.steps.forEach(function (s, n) { s.classList.toggle('is-active', n === i); });
      el.layers.forEach(function (l, n) { l.classList.toggle('is-active', n === i); });

      var pad = i + 1 < 10 ? '0' + (i + 1) : String(i + 1);
      if (el.numEl) el.numEl.textContent = pad;

      var step = el.steps[i];
      if (el.capEl && step) {
        var cap = step.getAttribute('data-hp-proc-cap');
        if (!cap) {
          var t = step.querySelector('.hp-abt-proc__step-title');
          cap = t ? t.textContent.trim() : '';
        }
        el.capEl.textContent = cap;
      }
      if (el.fillEl) el.fillEl.style.width = ((i + 1) / el.steps.length * 100) + '%';
    };

    return HpProcess;
  })();
  if (!customElements.get('hp-process')) customElements.define('hp-process', HpProcess);

  /* ---- <hp-journal-filter> — topic chips on the Journal index ------------
     Filters the posts already on the page rather than fetching anything: the
     whole index is a handful of blocks, so hiding and showing is instant and
     cannot half-fail. The chips are real buttons, so keyboard and screen
     readers get the same behaviour, and with JS off the bar is hidden by CSS
     and every post stays visible.
  ---------------------------------------------------------------------- */
  var HpJournalFilter = (function () {
    function HpJournalFilter() { return Reflect.construct(HTMLElement, [], HpJournalFilter); }
    HpJournalFilter.prototype = Object.create(HTMLElement.prototype, { constructor: { value: HpJournalFilter } });
    Object.setPrototypeOf(HpJournalFilter, HTMLElement);

    HpJournalFilter.prototype.connectedCallback = function () {
      var el = this;
      el.chips = Array.prototype.slice.call(el.querySelectorAll('[data-hp-topic]'));
      var scope = el.closest('.hp-jrnl') || document;
      el.posts = Array.prototype.slice.call(scope.querySelectorAll('[data-hp-post]'));
      if (!el.chips.length || !el.posts.length) return;

      el.chips.forEach(function (chip) {
        chip.addEventListener('click', function () { el.select(chip); });
      });
    };

    HpJournalFilter.prototype.select = function (chip) {
      var el = this;
      var topic = chip.getAttribute('data-hp-topic') || '';

      el.chips.forEach(function (c) {
        var on = c === chip;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      var shown = [];
      el.posts.forEach(function (post) {
        var match = !topic || post.getAttribute('data-hp-tag') === topic;
        post.hidden = !match;
        if (match) shown.push(post);
      });

      /* The reveal animations already ran for these posts, so re-running the
         motion engine would do nothing — animate the newly shown set here. */
      if (hasGsap && !reducedMotion && shown.length) {
        window.gsap.fromTo(shown,
          { y: 18, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out', stagger: 0.05, clearProps: 'transform,opacity' });
        window.ScrollTrigger.refresh();
      }
    };

    return HpJournalFilter;
  })();
  if (!customElements.get('hp-journal-filter')) customElements.define('hp-journal-filter', HpJournalFilter);

  /* ---- Timeline rail ----------------------------------------------------
     The rail fills in step with the scroll (scrubbed, so it tracks the finger
     rather than playing a canned animation) and each milestone lights its dot
     as it reaches reading height. Without GSAP the CSS already shows the rail
     filled, so only the dots need wiring.
  ---------------------------------------------------------------------- */
  function initTimeline(root) {
    (root || document).querySelectorAll('[data-hp-timeline]').forEach(function (wrap) {
      if (wrap.dataset.hpTimeDone) return;
      wrap.dataset.hpTimeDone = '1';

      var fill = wrap.querySelector('[data-hp-timeline-fill]');
      var items = Array.prototype.slice.call(wrap.querySelectorAll('[data-hp-timeline-item]'));

      if (fill && hasGsap) {
        window.gsap.fromTo(fill, { scaleY: 0 }, {
          scaleY: 1, ease: 'none',
          scrollTrigger: { trigger: wrap, start: 'top 72%', end: 'bottom 72%', scrub: true }
        });
      } else if (fill) {
        fill.style.transform = 'scaleY(1)';
      }

      if (!items.length) return;
      if (reducedMotion || !('IntersectionObserver' in window)) {
        items.forEach(function (it) { it.classList.add('is-active'); });
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-active');
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -28% 0px', threshold: 0.1 });
      items.forEach(function (it) { io.observe(it); });
    });
  }

  /* ---- Jump links — phone menu shortcuts to a section -------------------
     The href is a real URL (home page + anchor), so this only takes over when
     the target is on the page already: it closes the menu drawer first, then
     scrolls with the sticky header's height subtracted, which a plain anchor
     jump cannot do — the browser would leave the section's heading tucked
     underneath the header.
  ---------------------------------------------------------------------- */
  function headerOffset() {
    var raw = getComputedStyle(doc).getPropertyValue('--hp-header-h');
    var h = parseFloat(raw);
    if (!isNaN(h) && h > 0) return h + 12;
    var wrapper = document.querySelector('.header-wrapper');
    return (wrapper ? wrapper.offsetHeight : 0) + 12;
  }

  function scrollToEl(el) {
    var doScroll = function () {
      var top = el.getBoundingClientRect().top + window.scrollY - headerOffset();
      if (top < 0) top = 0;
      if (lenis) {
        /* Lenis is paused whenever an overlay holds the body scroll-lock, and
           the menu drawer only releases that lock when its close animation
           ends — so a scrollTo issued in the meantime was being dropped and
           the page never moved. Resume before asking it to scroll. */
        try { lenis.start(); } catch (e) {}
        lenis.scrollTo(top, { duration: 1.1 });
        return;
      }
      window.scrollTo({ top: top, behavior: reducedMotion ? 'auto' : 'smooth' });
    };

    doScroll();

    /* Once more after the drawer has finished closing: the lock may have been
       released late, or media above the target may have loaded and moved it. */
    window.setTimeout(function () {
      if (Math.abs(el.getBoundingClientRect().top - headerOffset()) > 40) doScroll();
    }, 480);
  }

  function closeMenuDrawer() {
    var drawer = document.querySelector('header-drawer');
    if (drawer && typeof drawer.closeMenuDrawer === 'function') {
      // Dawn's own close: restores focus, unlocks the body scroll and clears
      // the classes the overlay lock watches.
      try { drawer.closeMenuDrawer(); return; } catch (e) {}
    }
    var details = document.querySelector('header-drawer details[open]');
    if (details) details.removeAttribute('open');
    document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet');
  }

  /* ---- Menu drawer: close on any link ----------------------------------
     Tapping a menu item used to leave the drawer standing open — for a link to
     another page it stayed up until the new page painted, and for a link to
     the page you are already on (or an in-page one) nothing closed it at all.
     Delegated from the drawer itself so items added later are covered too.

     Only <a href> closes it: the submenu rows are <summary> elements, and
     closing the whole drawer when someone opens "Categories" would make the
     submenus unusable.
  ---------------------------------------------------------------------- */
  function initDrawerAutoClose(root) {
    (root || document).querySelectorAll('header-drawer').forEach(function (drawer) {
      if (drawer.dataset.hpAutoCloseDone) return;
      drawer.dataset.hpAutoCloseDone = '1';
      drawer.addEventListener('click', function (e) {
        var el = e.target;
        if (!el || !el.closest) return;
        var link = el.closest('a[href]');
        if (!link || el.closest('summary')) return;
        closeMenuDrawer();
      });
    });
  }

  /* ---- Desktop dropdown: close on any link ------------------------------
     Dawn's <header-menu> only closes its panel on focusout. That is enough
     for an ordinary menu link, where the click navigates and the page goes
     away — but a "Table Stories" item calls preventDefault to scroll instead,
     so focus never leaves the panel and it stayed open over the section the
     reader had just asked to see.

     Close the panel through the custom element's own close(), which also
     resets aria-expanded and lets Dawn's toggle bookkeeping run; the manual
     path is only a fallback for markup outside a header-menu.
  ---------------------------------------------------------------------- */
  function closeHeaderDropdown(el) {
    if (!el || !el.closest) return;
    var host = el.closest('header-menu, details-disclosure');
    if (host && typeof host.close === 'function') {
      try { host.close(); return; } catch (e) {}
    }
    var details = el.closest('details[open]');
    if (!details) return;
    details.removeAttribute('open');
    var summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  function initDropdownAutoClose(root) {
    (root || document).querySelectorAll('.header__inline-menu').forEach(function (nav) {
      if (nav.dataset.hpDropCloseDone) return;
      nav.dataset.hpDropCloseDone = '1';
      /* Delegated, so items rendered later are covered. Only <a href> closes
         it: the group rows are <summary>, and closing on those would undo the
         click that just opened the panel. */
      nav.addEventListener('click', function (e) {
        var el = e.target;
        if (!el || !el.closest) return;
        var link = el.closest('a[href]');
        if (!link || el.closest('summary')) return;
        closeHeaderDropdown(link);
      });
    });
  }

  function initJumpLinks(root) {
    (root || document).querySelectorAll('[data-hp-jump]').forEach(function (a) {
      if (a.dataset.hpJumpDone) return;
      a.dataset.hpJumpDone = '1';
      a.addEventListener('click', function (e) {
        var target = document.getElementById(a.getAttribute('data-hp-jump'));
        if (!target) return;          // another page — let the href do the work
        e.preventDefault();
        closeMenuDrawer();
        /* asserted here as well as in the delegated handler above: a jump link
           can sit outside .header__inline-menu, and this is the case that
           actually needs it — nothing else will close the panel. */
        closeHeaderDropdown(a);
        // let the drawer's own closing transition start before scrolling
        window.setTimeout(function () { scrollToEl(target); }, 60);
      });
    });
  }

  /* Arriving from another page as /#hp-colours: the browser lands the section
     under the sticky header, so nudge it into place once things have settled. */
  function honourHashOffset() {
    if (!window.location.hash) return;
    var el = document.getElementById(window.location.hash.slice(1));
    if (!el) return;
    window.setTimeout(function () { scrollToEl(el); }, 250);
  }

  /* ---- <hp-journal-reader> — open a post in place ------------------------
     A post can only have a URL of its own if a Shopify Page exists for it, and
     the theme cannot create pages, so every /pages/journal-… link 404'd. The
     posts are rendered into this page instead and shown at ?post=<handle>,
     which is a URL that genuinely resolves, survives a refresh and can be
     shared. History is pushed so Back returns to the index rather than leaving
     the site.
  ---------------------------------------------------------------------- */
  function initJournalReader(root) {
    var scope = root && root.querySelector ? root : document;
    var reader = scope.querySelector('[data-hp-journal-reader]');
    var index = scope.querySelector('[data-hp-journal-index]');
    if (!reader || !index || reader.dataset.hpReaderDone) return;
    reader.dataset.hpReaderDone = '1';

    var views = Array.prototype.slice.call(reader.querySelectorAll('[data-hp-post-view]'));
    var baseTitle = document.title;

    function slugFromUrl() {
      var m = window.location.search.match(/[?&]post=([^&]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    }

    function show(slug, scrollTop) {
      var found = null;
      views.forEach(function (v) {
        var on = v.getAttribute('data-hp-post-view') === slug;
        v.hidden = !on;
        if (on) found = v;
      });
      reader.classList.toggle('is-open', !!found);
      index.hidden = !!found;

      if (found) {
        var h = found.querySelector('.hp-jrnl__post-title');
        document.title = (h ? h.textContent.trim() + ' — ' : '') + baseTitle;
      } else {
        document.title = baseTitle;
      }
      if (scrollTop) {
        if (lenis) { lenis.scrollTo(0, { immediate: true }); } else { window.scrollTo(0, 0); }
      }
      if (hasGsap) window.ScrollTrigger.refresh();
      return !!found;
    }

    scope.querySelectorAll('[data-hp-post-link]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        // let modified clicks open a new tab as usual
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        var slug = a.getAttribute('data-hp-post-link');
        if (!slug) return;
        e.preventDefault();
        if (show(slug, true)) {
          window.history.pushState({ hpPost: slug }, '', a.getAttribute('href'));
        }
      });
    });

    scope.querySelectorAll('[data-hp-post-back]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        show('', true);
        // Drop the parameter from the URL we are actually on rather than
        // trusting the href — that keeps Back correct even if the link is
        // rendered without a path.
        var url = new URL(window.location.href);
        url.searchParams.delete('post');
        window.history.pushState({ hpPost: '' }, '', url.pathname + url.search + url.hash);
      });
    });

    window.addEventListener('popstate', function () { show(slugFromUrl(), false); });

    // first paint: a shared or refreshed ?post= link opens straight into the post
    show(slugFromUrl(), false);
  }

  /* ---- boot ------------------------------------------------------------- */
  function boot() {
    initOverlayScrollLock();
    markScrollContainers(document);
    initStickyChrome();
    initMotion(document);
    initVideoTriggers(document);
    initReviewPager(document);
    initStoreReviews(document);
    initReviewMarquee(document);
    initReviewCardFit(document);
    initBgVideo(document);
    initLazyPreviewVideos(document);
    initDealPopup(document);
    initProductZoom(document);
    initCounters(document);
    initTimeline(document);
    initJumpLinks(document);
    initDrawerAutoClose(document);
    initDropdownAutoClose(document);
    initJournalReader(document);
    honourHashOffset();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) {
    initMotion(e.target);
    initVideoTriggers(e.target);
    initReviewPager(e.target);
    initStoreReviews(e.target);
    initReviewMarquee(e.target);
    initReviewCardFit(e.target);
    initBgVideo(e.target);
    initLazyPreviewVideos(e.target);
    initDealPopup(e.target);
    initProductZoom(e.target);
    initCounters(e.target);
    initTimeline(e.target);
    initJumpLinks(e.target);
    initDrawerAutoClose(e.target);
    initDropdownAutoClose(e.target);
    initJournalReader(e.target);
    markScrollContainers(e.target);
    initStickyChrome();
    if (hasGsap) window.ScrollTrigger.refresh();
  });
  // cart drawer / quick-add inject markup without a section:load event
  document.addEventListener('shopify:afterCartUpdate', function () { initMotion(document); });
})();
